import { DatePipe, NgClass } from '@angular/common';
import { Component, DestroyRef, ElementRef, OnInit, ViewChild, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import dayjs from 'dayjs';
import { EmployeeCalendarRegisterRelatableType } from 'src/app/enums/employee-calendar-register-relatable-type.enum';
import {
  EmployeeCalendarRegisterNotificationRequestPayload,
  EmployeeCalendarRegisterRecurrenceAttributesConfig,
  EmployeeCalendarRegisterWithRelationsSerialized,
} from 'src/app/models/employee_calendar_register.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { lastValueFrom, firstValueFrom, catchError, concatMap, debounceTime, EMPTY, forkJoin, map, of, scan, Subject } from 'rxjs';
import {
  CalendarEventCustomized,
  WorkingTimesCalendarComponent,
  isAllowedEmployeeCalendarRegisterUserIdPayloadChange,
} from '../working-times-calendar.component';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { EmployeeService, UtilsService } from 'src/app/services';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent, MatOption, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import {
  CalendarEventParticipantCategoryAssignment,
  EmployeeCalendarRegisterUserSharedEvent,
} from 'src/app/models/employee_calendar_register_user_shared_event.model';
import { EmployeeCalendarRegisterNotificationRequest } from 'src/app/models/employee_calendar_register_notification_request.model';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { MatFormField, MatLabel, MatSuffix, MatHint, MatError } from '@angular/material/form-field';
import { CalendarEventDetailRecurrenceCustomizationDialogComponent } from './calendar-event-detail-recurrence-customization-dialog/calendar-event-detail-recurrence-customization-dialog.component';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { Location, ManageLocationDialogData } from 'src/app/models/location.model';
import { getLocationRelatableTypeLabel, LocationRelatableTypeEnum } from 'src/app/enums/location-relatable-type.enum';
import { ProductTaskBoard } from 'src/app/models/product_task_board.model';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { CompanyProductUserMember } from 'src/app/models/company_product_user_member.model';
import { CompanyProductPhase } from 'src/app/models/company_product_phase.model';
import { dateAfterStartValidator, dateBeforeEndValidator } from 'src/app/validators/date-after-start.validator';
import { locationNotSameAsValidator } from 'src/app/validators/location-not-same-as.validator';
import { ChannelsLabelPipe } from 'src/app/pipes/channels-label.pipe';
import { DecimalPipe } from '@angular/common';
import {
  CalendarEventNotificationDialogComponent,
  CalendarEventNotificationDialogData,
} from './calendar-event-notification-dialog/calendar-event-notification-dialog.component';
import { ManageLocationDialogComponent } from 'src/app/components/main/shared/locations/manage-location-dialog/manage-location-dialog.component';
import { CalendarEventParticipantCategoryDialogComponent } from './calendar-event-participant-category-dialog/calendar-event-participant-category-dialog.component';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { LocationDialogModeEnum } from 'src/app/enums/location-dialog-mode.enum';
import { DrivingRequest } from 'src/app/models/driving_request.model';

@Component({
  selector: 'app-calendar-event-detail-dialog',
  templateUrl: './calendar-event-detail-dialog.component.html',
  styleUrls: ['./calendar-event-detail-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CdkDrag,
    MatDialogTitle,
    CdkDragHandle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatCheckbox,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatButton,
    MatSelect,
    MatSelectTrigger,
    MatTooltip,
    MatOption,
    NgClass,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatHint,
    MatError,
    MatDialogActions,
    DatePipe,
    ChannelsLabelPipe,
    DecimalPipe,
  ],
})
export class CalendarEventDetailDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<CalendarEventDetailDialogComponent>>(MatDialogRef);
  data = inject<{
    event: CalendarEventCustomized | undefined;
    calendarComponent: WorkingTimesCalendarComponent;
    user_id: number;
    non_working_time_categories_that_appear_in_calendar: WorkingTimeCategory[];
    updatable: boolean;
  }>(MAT_DIALOG_DATA);
  private api = inject(ApiService);
  private user = inject(UserService);
  utils = inject(UtilsService);
  private datePipe = inject(DatePipe);
  private dialog = inject(MatDialog);
  private employee = inject(EmployeeService);

  private static readonly MADRID_TIMEZONE = 'Europe/Madrid';

  // config
  DAILY_FORM_CONFIG: EmployeeCalendarRegisterRecurrenceAttributesConfig;
  WEEKLY_FORM_CONFIG: EmployeeCalendarRegisterRecurrenceAttributesConfig;
  BIWEEKLY_FORM_CONFIG: EmployeeCalendarRegisterRecurrenceAttributesConfig;
  BYWEEKNO_FORM_CONFIG: EmployeeCalendarRegisterRecurrenceAttributesConfig;
  BYMONTH_FORM_CONFIG: EmployeeCalendarRegisterRecurrenceAttributesConfig;
  BYYEARDAY_FORM_CONFIG: EmployeeCalendarRegisterRecurrenceAttributesConfig;
  LABORALDAY_FORM_CONFIG: EmployeeCalendarRegisterRecurrenceAttributesConfig;
  EMPTY_RECURRENCE_FORM_CONFIG: EmployeeCalendarRegisterRecurrenceAttributesConfig;

  // others
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  event: CalendarEventCustomized | undefined;
  form: UntypedFormGroup;
  user_search_ctrl: FormControl = new FormControl();
  filtered_users_to_share: User[] = [];
  readonly default_profile_picture = 'assets/img/default_profile_picture.png';
  private readonly product_member_ids_by_product = new Map<number, Set<number>>();
  private lastFormValue: any;
  start_date_ctrl: FormControl = new FormControl();
  team_event_ctrl: FormControl = new FormControl();
  recurrence_ctrl: FormControl<
    '' | 'customized' | 'daily' | 'weekly' | 'biweekly' | 'byweekno' | 'bymonthday' | 'byyearday' | 'laboralday' | 'customize'
  > = new FormControl<
    '' | 'customized' | 'daily' | 'weekly' | 'biweekly' | 'byweekno' | 'bymonthday' | 'byyearday' | 'laboralday' | 'customize'
  >('') as FormControl<
    '' | 'customized' | 'daily' | 'weekly' | 'biweekly' | 'byweekno' | 'bymonthday' | 'byyearday' | 'laboralday' | 'customize'
  >;
  customRecurrenceText: string | undefined;
  private old_start: string;
  readonly updatable = signal<boolean>(false);
  readonly can_edit = signal(false);
  readonly permissions_loaded = signal(false);
  readonly is_owner = signal(false);
  readonly is_original_event_owner = signal(false);
  readonly is_persisted_event = signal(false);
  readonly owner_display_name = signal('');
  readonly has_original_event_owner_user = signal(false);
  readonly driving_request_exists = signal<boolean>(false);
  readonly should_disable_location_selectors = computed(
    () => this.driving_request_exists() || this.loading_locations() || !this.updatable()
  );
  readonly show_non_owner_cannot_delete_warning = computed(
    () =>
      this.permissions_loaded() &&
      !this.is_original_event_owner() &&
      this.has_original_event_owner_user() &&
      !this.can_edit() &&
      this.owner_display_name() !== ''
  );
  readonly show_non_owner_can_edit_instance_warning = computed(
    () => this.permissions_loaded() && !this.is_original_event_owner() && this.has_original_event_owner_user() && this.can_edit()
  );
  private readonly destroy_ref = inject(DestroyRef);
  private original_event_user_id: number | null = null;
  meeting_mode_company_product: null | number = null;
  @ViewChild('userSearchCtrlInput') userSearchCtrlInput!: ElementRef<HTMLInputElement>;

  // Selectors
  working_time_categories: WorkingTimeCategory[] = [];
  all_working_time_categories: WorkingTimeCategory[] = [];
  company_product_categories: WorkingTimeCategory[] = [];
  product_task_board: ProductTaskBoard | undefined;

  filtered_company_products: CompanyProduct[] = [];
  filtered_company_product_phases: CompanyProductPhase[] = [];

  readonly selected_working_time_categories = signal<WorkingTimeCategory[]>([]);

  private readonly working_time_category_lookup = new Map<string, WorkingTimeCategory>();
  private is_hydrating_category_tree = false;
  private permissions_hydration_done = false;
  private category_tree_hydration_done = false;

  private readonly working_time_category_children_changes = new Subject<WorkingTimeCategoryChildrenChange>();

  private readonly available_working_time_categories_by_level = toSignal(
    this.working_time_category_children_changes.pipe(
      // concatMap keeps clear/load/seed ordered so async children cannot land on the wrong level
      concatMap(change => {
        if (change.type === 'clear') {
          return of(change);
        }

        if (change.type === 'seed') {
          return of({
            type: 'loaded' as const,
            id: change.id,
            level: change.level,
            categories: change.categories,
          });
        }

        return this.api.getWorkingTimeCategoryChildren(change.id, { company_product_id: change.company_product_id }).pipe(
          map(
            (categories): WorkingTimeCategoryChildrenUpdate => ({
              type: 'loaded',
              id: change.id,
              level: change.level,
              categories,
            })
          ),
          catchError(() => EMPTY)
        );
      }),
      scan(
        (categories_by_level, update) => this.updateAvailableWorkingTimeCategoriesByLevel(categories_by_level, update),
        new Map<number, WorkingTimeCategory[]>()
      )
    ),
    { initialValue: new Map<number, WorkingTimeCategory[]>() }
  );

  readonly available_working_time_categories = computed((): WorkingTimeCategory[][] => {
    const categories_by_level = this.available_working_time_categories_by_level();
    const selected_categories = this.selected_working_time_categories();
    const available_categories: WorkingTimeCategory[][] = [];

    for (let level = 0; level < selected_categories.length; level++) {
      const categories = categories_by_level.get(level);

      if (categories == null) {
        break;
      }

      available_categories.push(categories);
    }

    return available_categories;
  });

  readonly subcategory_select_levels = computed((): WorkingTimeCategorySubcategorySelectLevel[] => {
    const selected_categories = this.selected_working_time_categories();
    const available_categories = this.available_working_time_categories();

    return available_categories.map(
      (categories, index): WorkingTimeCategorySubcategorySelectLevel => ({
        level: index + 1,
        form_control_name: `working_time_category_level_${index + 1}`,
        label: `Sub. de ${selected_categories[index]?.name ?? ''}`,
        categories,
      })
    );
  });

  is_company_product_category: boolean = false;
  is_company_product_phase_category: boolean = false;
  is_company_product_trips_category: boolean = false;
  shows_travel_time_checkbox: boolean = false;
  is_travel_time_checked: boolean = false;
  should_show_location_selectors: boolean = false;
  readonly locations = signal<LocationCustom[]>([]);
  readonly loading_locations = signal<boolean>(false);
  readonly CREATE_LOCATION_OPTION = '__create__';
  curr_origin_location_id: number | null | undefined;
  curr_destination_location_id: number | null | undefined;
  trip_distance_km: number | null | undefined;
  private location_control_for_create: 'origin_location_id' | 'destination_location_id' | null = null;
  readonly should_show_trip_distance_km = signal<boolean>(false);

  companyProductControl: UntypedFormControl = new UntypedFormControl('');
  companyProductPhaseControl: UntypedFormControl = new UntypedFormControl('');

  // Store selected company_product and phase for color/title updates
  selectedCompanyProduct: CompanyProduct | undefined;
  selectedCompanyProductPhase: CompanyProductPhase | undefined;

  // me
  me: User;

  // Notifications
  notifications: EmployeeCalendarRegisterNotificationRequest[] = [];
  deletedNotificationIds: number[] = [];
  loadingNotifications = false;
  notificationsChanged = false;

  constructor() {
    const data = this.data;
    const fb = inject(UntypedFormBuilder);
    const datePipe = this.datePipe;

    this.event = data.event ?? undefined;

    const default_start = dayjs().add(1, 'hour');
    const default_end = default_start.add(1, 'hour');

    this.form = fb.group({
      id: [this.event?.meta.ecr.id ?? 'tmp_' + Math.random() * 100],
      custom_title: [this.event?.meta.ecr.custom_title],
      description: [this.event?.meta.ecr.description],
      call_link: [
        this.event?.meta.ecr.call_link,
        this.event?.meta.ecr.online_conference_type === 'external' ? [Validators.required, Validators.pattern(this.utils.urlRegex())] : [],
      ],
      meet_mode: [this.event?.meta.ecr.meet_mode ?? false],
      start: [
        this.event?.allDay
          ? this.formatAllDayDateForForm(this.event.start)
          : datePipe.transform((this.event?.start ?? default_start.toDate()).toISOString(), 'yyyy-MM-ddTHH:mm:ss')!.slice(0, 16),
        [Validators.required, dateBeforeEndValidator('end')],
      ],
      end: [
        this.event != null
          ? this.event?.allDay
            ? this.formatAllDayDateForForm(this.event.end ?? this.event.start)
            : datePipe
                .transform(this.event.end != null ? this.event.end!.toISOString() : this.event.start!.toISOString(), 'yyyy-MM-ddTHH:mm:ss')!
                .slice(0, 16)
          : datePipe.transform(default_end.toDate().toISOString(), 'yyyy-MM-ddTHH:mm:ss')!.slice(0, 16),
        [Validators.required, dateAfterStartValidator('start')],
      ],
      employee_calendar_register_user_shared_events: [this.data.event?.meta.ecr?.employee_calendar_register_user_shared_events ?? []],
      online_conference_type: [this.event?.meta.ecr.online_conference_type],
      is_blocked_for_meetings: [this.event?.meta.ecr.is_blocked_for_meetings ?? false],
      working_time_category: [this.event?.meta.ecr.working_time_category, Validators.required],
      user_id: [this.event?.meta.ecr != null ? this.event?.meta.ecr.user_id : this.user.getCurrentUser().id],
      all_day: [this.event?.allDay ?? false],

      // recurrence
      parent_recurrent_employee_calendar_register_id: [this.data.event?.meta.ecr?.parent_recurrent_employee_calendar_register_id],
      recurrence_freq: [this.data.event?.meta.ecr?.recurrence_freq],
      recurrence_interval: [this.data.event?.meta.ecr?.recurrence_interval],
      recurrence_ending_count: [this.data.event?.meta.ecr?.recurrence_ending_count],
      recurrence_ending_until: [this.data.event?.meta.ecr?.recurrence_ending_until],
      recurrence_byday: [this.data.event?.meta.ecr?.recurrence_byday],
      recurrence_bymonthday: [this.data.event?.meta.ecr?.recurrence_bymonthday],
      recurrence_byyearday: [this.data.event?.meta.ecr?.recurrence_byyearday],
      recurrence_byweekno: [this.data.event?.meta.ecr?.recurrence_byweekno],
      recurrence_bymonth: [this.data.event?.meta.ecr?.recurrence_bymonth],
      recurrence_bysetpos: [this.data.event?.meta.ecr?.recurrence_bysetpos],

      company_product_id: [''],
      product_task_board_id: [''],
      company_product_phase_id: [''],
      relatable_type: [this.event?.meta.ecr?.relatable_type ?? null],
      relatable_id: [this.event?.meta.ecr?.relatable_id ?? null],
      working_time_category_id: [this.event?.meta.ecr?.working_time_category_id ?? null],

      // Dynamic category level fields (up to 5 levels)
      working_time_category_level_0: [''],
      working_time_category_level_1: [''],
      working_time_category_level_2: [''],
      working_time_category_level_3: [''],
      working_time_category_level_4: [''],

      origin_location_id: [
        this.toLocationId(this.event?.meta.ecr?.origin_location_id),
        locationNotSameAsValidator('destination_location_id'),
      ],
      destination_location_id: [
        this.toLocationId(this.event?.meta.ecr?.destination_location_id),
        locationNotSameAsValidator('origin_location_id'),
      ],
    });
    this.lastFormValue = this.form.value;
    this.start_date_ctrl.patchValue(this.form.value.start);
    this.old_start = this.start_date_ctrl.value;
    this.team_event_ctrl.patchValue(this.form.value.user_id == null || this.form.value.user_id == '');
    this.me = this.user.getCurrentUser();
    this.initOriginalEventOwnership();
    this.syncOwnershipFromFormUserId(this.form.value.user_id);
    this.updatable.set(false);
    this.initMyEventPermissions();

    this.curr_origin_location_id = this.event?.meta.ecr?.origin_location_id;
    this.curr_destination_location_id = this.event?.meta.ecr?.destination_location_id;
    this.trip_distance_km = this.event?.meta.ecr?.trip_distance_km;
    this.should_show_trip_distance_km.set(
      this.curr_origin_location_id !== null &&
        this.curr_origin_location_id !== undefined &&
        this.curr_destination_location_id !== null &&
        this.curr_destination_location_id !== undefined &&
        this.curr_origin_location_id === this.form.value.origin_location_id &&
        this.curr_destination_location_id === this.form.value.destination_location_id &&
        this.trip_distance_km !== null &&
        this.trip_distance_km !== undefined
    );

    this.initFilteredUsersToShare();
    this.initFormConfigDefaults();

    // això s'ha de fer després de initFormConfigDefaults
    const rec_value_ctrl = this.transformEcrToRecurrenceCtrlValue();
    this.recurrence_ctrl.patchValue(rec_value_ctrl);

    if (rec_value_ctrl === 'customized') this.initCustomTextRecurrence();
  }

  ngOnInit(): void {
    this.fetchWorkingTimeCategories();
    this.listenCompanyProductControl();
    this.listenFormChangesToTransmitToParentComponent();
    this.listenOnlineConferenceTypeInput();
    this.listenStartInput();
    this.listenEndInput();
    this.listenSharedUsersInput();
    this.listenAllDayInput();
    this.listenTeamEventCtrl();
    this.listenRecurrentCtrl();
    this.listenMeetingModeCompanyProduct();
    this.fetchNotifications();
  }

  openNotificationDialog(notification?: EmployeeCalendarRegisterNotificationRequest) {
    const dialogData: CalendarEventNotificationDialogData = {
      notification: notification || null,
    };

    const dialogRef = this.dialog.open(CalendarEventNotificationDialogComponent, {
      width: '400px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: Partial<EmployeeCalendarRegisterNotificationRequest> | undefined) => {
      const event_id = this.getEventEcr()?.id ?? this.form.value.id;
      if (result && event_id) {
        const fullNotification: EmployeeCalendarRegisterNotificationRequest = {
          id: result.id ?? 0,
          user_id: this.data.user_id,
          employee_calendar_register_id: typeof event_id === 'number' ? event_id : 0,
          minutes_before: result.minutes_before!,
          channels: result.channels!,
          notified_at: result.notified_at,
          created_at: result.created_at ?? new Date(),
          updated_at: result.updated_at ?? new Date(),
        };

        if (notification) {
          const index = this.notifications.findIndex(n => n.id === notification.id);
          if (index !== -1) {
            this.notifications[index] = fullNotification;
          }
        } else {
          this.notifications.push(fullNotification);
        }
        this.notificationsChanged = true;
        this.syncNotificationsToParent();
      }
    });
  }

  deleteNotification(notification: EmployeeCalendarRegisterNotificationRequest) {
    const index = this.notifications.findIndex(n => n.id === notification.id);
    if (index !== -1) {
      // Mark for deletion if it has an ID, otherwise just remove from array
      if (notification.id && notification.id > 0) {
        // Mark for deletion and remove from visible list
        this.notifications.splice(index, 1);
        // Store deleted IDs separately to send to backend
        this.deletedNotificationIds.push(notification.id);
      } else {
        this.notifications.splice(index, 1);
      }
      this.notificationsChanged = true;
      this.syncNotificationsToParent();
    }
  }

  getChannelIcon(channel: string): string {
    switch (channel) {
      case 'email':
        return 'email';
      case 'slack':
        return 'chat';
      case 'calendar_app':
        return 'calendar_today';
      default:
        return 'notifications';
    }
  }

  getChannelLabel(channel: string): string {
    switch (channel) {
      case 'email':
        return 'Correo electrónico';
      case 'slack':
        return 'Slack';
      case 'calendar_app':
        return 'Notificación';
      default:
        return channel;
    }
  }

  close() {
    this.dialogRef.close();
  }

  // Custom validator to check if form is valid based on category requirements
  private validateCategoryRequirements(): CategoryRequirementErrors | null {
    const errors: CategoryRequirementErrors = {};
    let has_errors = false;

    const selected_categories = this.selected_working_time_categories();
    const final_category = selected_categories[selected_categories.length - 1];

    if (!final_category) {
      return { categoryRequired: true };
    }

    // Check if category requires company_product and company_product is not selected
    // This can happen if is_company_product_category is true OR if needs_relation_when_registering_time is 'company_product'
    const requires_company_product =
      this.is_company_product_category || final_category.needs_relation_when_registering_time === 'company_product';
    if (requires_company_product && !this.form.value.company_product_id) {
      errors.companyProductRequired = true;
      has_errors = true;
    }

    // Check if category requires company_product phase and phase is not selected
    // This can happen if is_company_product_phase_category is true OR if needs_relation_when_registering_time is 'company_product_phase'
    const requires_company_product_phase =
      this.is_company_product_phase_category || final_category.needs_relation_when_registering_time === 'company_product_phase';
    if (requires_company_product_phase && !this.form.value.company_product_phase_id) {
      errors.companyProductPhaseRequired = true;
      has_errors = true;
    }

    // Origin and destination locations are mandatory for trips (project trips or travel time)
    if (this.should_show_location_selectors) {
      if (!this.form.value.origin_location_id) {
        errors.originLocationRequired = true;
        has_errors = true;
      }
      if (!this.form.value.destination_location_id) {
        errors.destinationLocationRequired = true;
        has_errors = true;
      }
    }

    // Check if there are available subcategories that haven't been selected
    // The available_working_time_categories array contains subcategories for each level
    // available_working_time_categories[i] contains subcategories for the category selected at level i
    // We only need to check if a category at level i has subcategories available (available_working_time_categories[i])
    // AND no category has been selected at level i+1
    // Important: We only check up to the last level where we have a selected category
    const available_working_time_categories = this.available_working_time_categories();

    if (available_working_time_categories.length > 0 && selected_categories.length > 0) {
      // Check each level: if there are subcategories available for a selected category,
      // we must have selected one of those subcategories
      // But only check up to the level where we have a selected category
      const max_level_to_check = Math.min(available_working_time_categories.length, selected_categories.length);

      for (let index = 0; index < max_level_to_check; index++) {
        const level = index + 1; // level 0 is the main category, subcategories start at level 1
        const available_categories = available_working_time_categories[index];

        // If there are available categories at this level (subcategories of the category at level i)
        // AND we have selected a category at level i (meaning we should select a subcategory)
        // THEN we must have selected a category at level i+1
        if (available_categories && available_categories.length > 0) {
          // Check if we have a category selected at level i (the parent)
          const has_parent_selected = index < selected_categories.length && selected_categories[index] != null;

          if (has_parent_selected) {
            // If parent is selected and has subcategories, we must select one
            const selected_category_id = this.form.value[`working_time_category_level_${level}`];
            if (!selected_category_id || selected_category_id === '') {
              // Get the parent category name for the error message
              const parent_category = selected_categories[index];
              errors.subcategoryRequired = {
                level: level,
                categoryName: parent_category?.name || 'categoría',
              };
              has_errors = true;
              break; // Only show error for first missing subcategory
            }
          }
        }
      }
    }

    return has_errors ? errors : null;
  }

  get isFormValid(): boolean {
    const required_fields = [
      'start',
      'end',
      'working_time_category',
      ...(this.should_show_location_selectors ? ['origin_location_id', 'destination_location_id'] : []),
    ];
    const hasRequiredFieldErrors = required_fields.some(fieldName => this.hasBlockingFieldErrors(fieldName));

    if (hasRequiredFieldErrors) {
      return false;
    }

    const categoryErrors = this.validateCategoryRequirements();
    if (categoryErrors) {
      return false;
    }

    return true;
  }

  private hasBlockingFieldErrors(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    if (!control?.invalid || !control.errors) {
      return false;
    }

    const errors = { ...control.errors };

    if (this.form.value.all_day && (fieldName === 'start' || fieldName === 'end')) {
      delete errors['dateAfterStart'];
      delete errors['dateBeforeEnd'];
    }

    return Object.keys(errors).length > 0;
  }

  get canSave(): boolean {
    if (this.updatable()) {
      if (this.isCreate()) {
        return this.isFormValid;
      }

      return this.isFormValid && (this.isDialogFormDirty() || this.notificationsChanged);
    }

    return this.notificationsChanged;
  }

  locationName(id: number | null | undefined): string {
    if (id == null) {
      return 'Sin seleccionar';
    }

    return this.locations().find(location => location.id === id)?.name ?? '';
  }

  locationTypeLabel(location: Location): string {
    return getLocationRelatableTypeLabel(location.relatable_type);
  }

  onLocationSelectionChange(control_name: 'origin_location_id' | 'destination_location_id', value: unknown): void {
    if (value !== this.CREATE_LOCATION_OPTION) {
      return;
    }

    this.location_control_for_create = control_name;
    this.form.patchValue({ [control_name]: null });
    this.openManageLocationDialog();
  }

  openManageLocationDialog(): void {
    if (!this.should_show_location_selectors) {
      return;
    }

    const data: ManageLocationDialogData = { choose_relatable: true };

    this.dialog
      .open(ManageLocationDialogComponent, {
        width: '980px',
        maxWidth: '96vw',
        data: data satisfies ManageLocationDialogData,
      })
      .afterClosed()
      .subscribe((created: Location | undefined) => {
        this.onLocationCreated(created, () => this.api.getLocations({ scopes: 'relatedToUser' }));
      });
  }

  openLocationDialog(location: Location, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.dialog.open(ManageLocationDialogComponent, {
      data: { mode: LocationDialogModeEnum.View, location: location } satisfies ManageLocationDialogData,
    });
  }

  private onLocationCreated(created: Location | undefined, refresh: () => ReturnType<ApiService['getLocations']>): void {
    const control_name = this.location_control_for_create;
    this.location_control_for_create = null;

    if (created == null) {
      return;
    }

    this.upsertCreatedLocation(created);
    this.selectCreatedLocation(created.id, control_name);

    refresh().subscribe((response: ArrayResponse<Location>): void => {
      this.locations.set(this.mergeCreatedLocation(response.data ?? [], created));
      this.selectCreatedLocation(created.id, control_name);
    });
  }

  private upsertCreatedLocation(created: Location): void {
    this.locations.update(locations => this.mergeCreatedLocation(locations, created));
  }

  private mergeCreatedLocation(locations: Location[], created: Location): LocationCustom[] {
    const options = this.toLocationOptions(locations);
    if (options.some(location => location.id === created.id)) {
      return options;
    }

    return [...options, ...this.toLocationOptions([created])];
  }

  private selectCreatedLocation(location_id: number, control_name: 'origin_location_id' | 'destination_location_id' | null): void {
    if (control_name != null) {
      this.form.patchValue({ [control_name]: location_id });
      return;
    }

    if (!this.form.value.origin_location_id) {
      this.form.patchValue({ origin_location_id: location_id });
    } else if (!this.form.value.destination_location_id) {
      this.form.patchValue({ destination_location_id: location_id });
    }
  }

  addMinutes(minutes: number) {
    //end date is start date + minutes
    const start = new Date(this.form.value.start);
    const end = new Date(start.getTime() + minutes * 60000);
    const datePipe = new DatePipe('es-ES');
    this.form.patchValue({ end: datePipe.transform(end!.toISOString(), 'yyyy-MM-ddTHH:mm:ss')!.slice(0, 16) });
  }

  async save(): Promise<void> {
    if (!this.isFormValid) {
      const errors = this.validateCategoryRequirements();
      if (errors) {
        if (errors.companyProductRequired) {
          alert('Debes seleccionar un producto para esta categoría.');
        } else if (errors.companyProductPhaseRequired) {
          alert('Debes seleccionar una fase de producto para esta categoría.');
        } else if (errors.subcategoryRequired) {
          alert(`Debes seleccionar una subcategoría de "${errors.subcategoryRequired.categoryName}".`);
        } else if (errors.travelCategoryRequired) {
          alert('Debes seleccionar a qué categoría pertenece el tiempo de desplazamiento.');
        } else if (errors.originLocationRequired || errors.destinationLocationRequired) {
          alert('Debes seleccionar la ubicación de origen y la de destino.');
        }
      }
      return;
    }

    const ecrs: EmployeeCalendarRegisterWithRelationsSerialized = this.transformFormToEcrSerialized();
    const presentation_keys = new Set([
      'working_time_category',
      'working_time_category_id',
      'wtc_slug',
      'wtc_color',
      'wtc_name',
      'company_product_color',
      'company_product_title',
      'company_product_phase_name',
      'relatable_type',
      'relatable_id',
    ]);

    for (const key of Object.keys(ecrs) as (keyof EmployeeCalendarRegisterWithRelationsSerialized)[]) {
      if (ecrs[key] == undefined && !presentation_keys.has(key as string)) {
        (ecrs[key] as any) = null;
      }
    }

    const conflicts = await this.data.calendarComponent.getAttendeeCalendarConflicts(
      ecrs.from_date,
      ecrs.to_date,
      this.data.calendarComponent.getAttendeeUsersFromEcr(ecrs),
      ecrs.all_day,
      ecrs.id,
      undefined
    );

    if (!(await this.data.calendarComponent.askProceedDespiteAttendeeConflicts(conflicts))) {
      return;
    }

    this.dialogRef.close(ecrs);
  }

  deleteEvent(): void {
    this.dialogRef.close({ calendar_dialog_action: 'delete' as const });
  }

  getAnonymousUserDisplayName(shared_user: EmployeeCalendarRegisterUserSharedEvent) {
    if (shared_user.anonymous_user_email != null) return shared_user.anonymous_user_email;
    const user = shared_user.user;
    if (user) {
      return `${user.name} ${user.surnames}`.trim();
    }
    return '#' + shared_user.user_id;
  }

  ownerParticipantName(): string {
    const name = this.owner_display_name();
    if (name) {
      return name;
    }
    return `${this.me.name} ${this.me.surnames}`.trim();
  }

  ownerPhotoUrl(): string {
    const ecr = this.event?.meta.ecr;
    if (ecr?.user?.profile_picture_url) {
      return ecr.user.profile_picture_url;
    }
    const owner_id = this.form.value.user_id ?? ecr?.user_id;
    if (owner_id == this.me.id) {
      return this.me.profile_picture_url || this.default_profile_picture;
    }
    const from_calendar = this.data.calendarComponent?.userOptions?.find(user => user.id == owner_id);
    if (from_calendar?.profile_picture_url) {
      return from_calendar.profile_picture_url;
    }
    return this.default_profile_picture;
  }

  sharedUserPhotoUrl(shared_user: EmployeeCalendarRegisterUserSharedEvent): string {
    return shared_user.user?.profile_picture_url || this.default_profile_picture;
  }

  ownerCategoryLabel(): string {
    return this.categoryAssignmentLabel(this.ownerCategoryAssignment());
  }

  sharedUserCategoryLabel(shared_user: EmployeeCalendarRegisterUserSharedEvent): string {
    return this.categoryAssignmentLabel(this.sharedUserToAssignment(shared_user));
  }

  async openParticipantCategoryDialog(target: 'owner' | number): Promise<void> {
    if (!this.updatable()) {
      return;
    }

    const participant_name =
      target === 'owner' ? this.ownerParticipantName() : this.getAnonymousUserDisplayName(this.sharedUsers()[target]);
    const assignment = target === 'owner' ? this.ownerCategoryAssignment() : this.sharedUserToAssignment(this.sharedUsers()[target]);
    const participant_user_id = target === 'owner' ? Number(this.form.value.user_id) : this.sharedUsers()[target].user_id;
    const dialogRef = this.dialog.open(CalendarEventParticipantCategoryDialogComponent, {
      width: '480px',
      data: {
        participant_name,
        user_id: participant_user_id || null,
        assignment: assignment.working_time_category_id ? assignment : null,
        working_time_categories: this.working_time_categories,
        all_working_time_categories: this.all_working_time_categories,
      },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result == null) {
      return;
    }

    await this.applyCategoryAssignment(target, result, true);
  }

  displayFnCompanyProduct(p: CompanyProduct): string {
    return p ? p.title : '';
  }

  displayFnCompanyProductPhase(phase: CompanyProductPhase): string {
    return phase.company_product_phase_type.name;
  }

  resetCompanyProduct() {
    this.selectedCompanyProduct = undefined;
    this.selectedCompanyProductPhase = undefined;
    this.form.patchValue({ company_product_id: '' });
    this.companyProductControl.setValue('');
    this.clearAvailableWorkingTimeCategoriesFromLevel(0);
    this.selected_working_time_categories.update(categories => (categories[0] != null ? [categories[0]] : []));
    this.is_company_product_phase_category = false;
    this.syncTripState();
  }

  selectCompanyProduct(company_product: CompanyProduct): void {
    const previous_product_id = this.selectedCompanyProduct?.id;
    const product_changed = previous_product_id != null && previous_product_id !== company_product.id;
    const selected_categories = this.selected_working_time_categories();
    const root_category = selected_categories[0];

    this.selectedCompanyProduct = company_product;
    this.form.patchValue({
      company_product_id: company_product.id,
      relatable_id: company_product.id,
      relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProduct,
    });

    if (!this.is_hydrating_category_tree) {
      this.form.markAsDirty();
      this.companyProductControl.markAsDirty();
    }

    if (root_category == null) {
      return;
    }

    if (product_changed && selected_categories.length > 1) {
      this.resetCompanyProductSubcategories(root_category);
    }

    // Always reload children of the root "Producto" category for the selected product
    this.fetchWorkingTimeCategoryChildren(root_category.id.toString(), 0);

    void this.cacheProductMemberIds(company_product.id);

    // Refresh trips locations, as they depend on the selected company's product
    this.syncTripState();

    if (this.is_company_product_trips_category) {
      this.fetchCompanyProductTripsLocations();
    }
  }

  private async cacheProductMemberIds(company_product_id: number): Promise<Set<number>> {
    const product_id = Number(company_product_id);
    if (!Number.isFinite(product_id) || product_id <= 0) {
      return new Set();
    }

    const cached = this.product_member_ids_by_product.get(product_id);
    if (cached) {
      return cached;
    }

    const data = await lastValueFrom(this.api.getActiveCompanyProductMembersWithBusiness(product_id.toString()));
    const member_ids = new Set<number>();
    data.bisual_users.forEach((member: CompanyProductUserMember): void => {
      member_ids.add(member.user.id);
    });
    data.company_users.forEach((client: User) => {
      member_ids.add(client.id);
    });
    data.account_manager_users.forEach((account_manager: User): void => {
      member_ids.add(account_manager.id);
    });
    this.product_member_ids_by_product.set(product_id, member_ids);
    return member_ids;
  }

  resetCompanyProductPhase() {
    this.selectedCompanyProductPhase = undefined;
    this.form.patchValue({
      company_product_phase_id: '',
      relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProduct,
      relatable_id: this.form.value['company_product_id'],
    });
    this.companyProductPhaseControl.setValue('');
  }

  removeSharedUser(idx: number) {
    const value: EmployeeCalendarRegisterUserSharedEvent[] = [...this.form.value.employee_calendar_register_user_shared_events];
    value.splice(idx, 1);
    this.form.controls['employee_calendar_register_user_shared_events'].patchValue(value);
    this.form.controls['employee_calendar_register_user_shared_events'].markAsDirty();
    this.user_search_ctrl.setValue('');
    this.user_search_ctrl.reset();
  }

  onSharedUserEmailEntered(): void {
    const value = this.user_search_ctrl.value;
    if (typeof value === 'string' && this.utils.isValidEmail(value)) {
      this.onSharedUserInputTokenEnd({ value, chipInput: { clear: () => undefined } } as MatChipInputEvent);
    }
  }

  onSharedUserInputTokenEnd(evt: MatChipInputEvent) {
    const user_id_or_email: number | string = evt.value;
    if (
      user_id_or_email != null &&
      ((typeof user_id_or_email === 'string' && this.utils.isValidEmail(user_id_or_email)) || typeof user_id_or_email === 'number') &&
      !this.form.value.employee_calendar_register_user_shared_events.find(
        (shared_user: EmployeeCalendarRegisterUserSharedEvent) =>
          shared_user.anonymous_user_email === user_id_or_email || shared_user.user_id === +user_id_or_email
      )
    ) {
      void this.addNewSharedUser(user_id_or_email);
    }
    this.user_search_ctrl.setValue('');
    this.user_search_ctrl.reset();
  }

  async onSelectedOptionAutocompleteForSharedUser(evt: MatAutocompleteSelectedEvent) {
    const user: User = evt.option.value;
    this.clearSharedUserSearch();

    if (
      !(this.form.value.employee_calendar_register_user_shared_events as EmployeeCalendarRegisterUserSharedEvent[]).find(
        ecruse => ecruse.user_id == user.id
      )
    ) {
      await this.addNewSharedUser(user);
    }
  }

  displayFnSharedUser = (user: User | string | null): string => {
    if (user == null || typeof user === 'string') {
      return typeof user === 'string' ? user : '';
    }
    return `${user.name ?? ''} ${user.surnames ?? ''}`.trim();
  };

  private clearSharedUserSearch(): void {
    this.user_search_ctrl.setValue('', { emitEvent: false });
    this.user_search_ctrl.reset('', { emitEvent: false });
    this.filtered_users_to_share = [];
    if (this.userSearchCtrlInput?.nativeElement) {
      this.userSearchCtrlInput.nativeElement.value = '';
    }
  }

  onEndFocus() {
    const end: string = this.form.value.end;
    if ((end === '' || end == null) && this.form.value.start != '') {
      if (this.form.value.all_day) {
        this.form.controls['end'].patchValue(this.formatAllDayDateForForm(this.form.value.start));
        return;
      }

      this.form.controls['end'].patchValue(
        this.datePipe.transform(dayjs(this.form.value.start).add(1, 'hour').toDate().toISOString(), 'yyyy-MM-ddTHH:mm:ss')!.slice(0, 16)
      );
    }
  }

  private listenCompanyProductControl() {
    this.companyProductControl.valueChanges.subscribe(data => {
      this.api.getCompanyProducts({ search: data }).subscribe(data => {
        this.filtered_company_products = data.data;
      });
    });

    this.companyProductPhaseControl.valueChanges.subscribe(data => {
      this.api
        .getCompanyProductPhases({ search: data, company_product_id: this.form.value['company_product_id'], is_active: true })
        .subscribe(data => {
          this.filtered_company_product_phases = data.data;
        });
    });
  }

  private async addNewSharedUser(user_or_email: string | User, wtc: WorkingTimeCategory | undefined = undefined) {
    if (typeof user_or_email !== 'string' && typeof user_or_email !== 'number') {
      const user = user_or_email;
      const event_dates = this.getEventDatesFromForm();

      if (event_dates !== null) {
        const conflicts = await this.data.calendarComponent.getAttendeeCalendarConflicts(
          event_dates.from_date,
          event_dates.to_date,
          [user],
          event_dates.all_day,
          event_dates.id,
          [user.id]
        );

        if (!(await this.data.calendarComponent.askProceedDespiteAttendeeConflicts(conflicts))) {
          return;
        }
      }
    }

    const value: EmployeeCalendarRegisterUserSharedEvent[] = [...this.form.value.employee_calendar_register_user_shared_events];
    if (typeof user_or_email === 'string') {
      value.push({
        employee_calendar_register_id: this.event?.meta.ecr.id as number,
        user_id: undefined,
        anonymous_user_email: user_or_email,
        working_time_category: undefined,
        working_time_category_id: null,
        relatable_type: null,
        relatable_id: null,
      } as EmployeeCalendarRegisterUserSharedEvent);
    } else {
      const default_assignment = await this.defaultAssignmentForUser(user_or_email);
      value.push({
        employee_calendar_register_id: this.event?.meta.ecr.id as number,
        user_id: user_or_email.id,
        anonymous_user_email: undefined,
        user: user_or_email,
        working_time_category: default_assignment?.working_time_category ?? wtc,
        working_time_category_id: default_assignment?.working_time_category_id ?? wtc?.id ?? null,
        relatable_type: default_assignment?.relatable_type ?? null,
        relatable_id: default_assignment?.relatable_id ?? null,
        company_product_title: default_assignment?.company_product_title ?? null,
        company_product_phase_name: default_assignment?.company_product_phase_name ?? null,
      } as EmployeeCalendarRegisterUserSharedEvent);
    }

    this.form.controls['employee_calendar_register_user_shared_events'].patchValue(value);
    this.form.controls['employee_calendar_register_user_shared_events'].markAsDirty();

    if (this.ownerCategoryAssignment().working_time_category_id == null) {
      await this.openParticipantCategoryDialog('owner');
    }
  }

  private sharedUsers(): EmployeeCalendarRegisterUserSharedEvent[] {
    return [...(this.form.value.employee_calendar_register_user_shared_events ?? [])];
  }

  uniqueSharedUsers(): EmployeeCalendarRegisterUserSharedEvent[] {
    const owner_id = this.form.value.user_id;
    const seen = new Set<string>();

    return this.sharedUsers().filter(shared_user => {
      if (shared_user.user_id != null && owner_id != null && Number(shared_user.user_id) === Number(owner_id)) {
        return false;
      }
      const key = shared_user.user_id != null ? `u:${shared_user.user_id}` : `e:${shared_user.anonymous_user_email ?? ''}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  indexOfSharedUser(shared_user: EmployeeCalendarRegisterUserSharedEvent): number {
    return this.sharedUsers().indexOf(shared_user);
  }

  private ownerCategoryAssignment(): CalendarEventParticipantCategoryAssignment {
    const category = this.form.value.working_time_category as WorkingTimeCategory | null;
    return {
      working_time_category_id: category?.id ?? this.form.value.working_time_category_id ?? null,
      working_time_category: category,
      relatable_type: this.form.value.relatable_type ?? null,
      relatable_id: this.form.value.relatable_id ?? null,
      company_product_id: this.selectedCompanyProduct?.id ?? this.normalizedCompanyProductId(this.form.value.company_product_id),
      company_product_title: this.selectedCompanyProduct?.title ?? this.event?.meta.ecr?.company_product_title ?? null,
      company_product_color: this.selectedCompanyProduct?.color ?? this.event?.meta.ecr?.company_product_color ?? null,
      company_product_phase_name:
        this.selectedCompanyProductPhase?.company_product_phase_type?.name ?? this.event?.meta.ecr?.company_product_phase_name ?? null,
    };
  }

  private sharedUserToAssignment(shared_user: EmployeeCalendarRegisterUserSharedEvent): CalendarEventParticipantCategoryAssignment {
    return {
      working_time_category_id: shared_user.working_time_category_id ?? shared_user.working_time_category?.id ?? null,
      working_time_category: shared_user.working_time_category,
      relatable_type: shared_user.relatable_type ?? null,
      relatable_id: shared_user.relatable_id ?? null,
      company_product_id:
        shared_user.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProduct
          ? shared_user.relatable_id
          : (shared_user.relatable?.company_product?.id ?? null),
      company_product_title:
        shared_user.company_product_title ?? shared_user.relatable?.title ?? shared_user.relatable?.company_product?.title ?? null,
      company_product_color: shared_user.company_product_color ?? null,
      company_product_phase_name: shared_user.company_product_phase_name ?? shared_user.relatable?.company_product_phase_type?.name ?? null,
    };
  }

  private categoryAssignmentLabel(assignment: CalendarEventParticipantCategoryAssignment): string {
    if (assignment.working_time_category_id == null) {
      return 'Asignar categoría';
    }
    const category_name = assignment.working_time_category?.name ?? 'Categoría';
    if (assignment.company_product_title) {
      return `${assignment.company_product_title} → ${category_name}`;
    }
    return category_name;
  }

  private async applyCategoryAssignment(
    target: 'owner' | number,
    assignment: CalendarEventParticipantCategoryAssignment,
    propagate: boolean
  ): Promise<void> {
    if (target === 'owner') {
      this.applyOwnerCategoryAssignment(assignment);
    } else {
      this.patchSharedUserAssignment(target, assignment);
    }

    if (!propagate) {
      return;
    }

    const shared_users = this.sharedUsers();
    for (let index = 0; index < shared_users.length; index++) {
      if (target === index) {
        continue;
      }
      const shared_user = shared_users[index];
      if (shared_user.user_id == null || this.sharedUserToAssignment(shared_user).working_time_category_id != null) {
        continue;
      }
      if (await this.userCanUseAssignment(shared_user.user_id, assignment)) {
        this.patchSharedUserAssignment(index, assignment);
      }
    }

    if (target !== 'owner' && this.ownerCategoryAssignment().working_time_category_id == null) {
      const owner_id = this.form.value.user_id;
      if (owner_id != null && (await this.userCanUseAssignment(Number(owner_id), assignment))) {
        this.applyOwnerCategoryAssignment(assignment);
      }
    }
  }

  private applyOwnerCategoryAssignment(assignment: CalendarEventParticipantCategoryAssignment): void {
    this.form.patchValue({
      working_time_category: assignment.working_time_category,
      working_time_category_id: assignment.working_time_category_id,
      relatable_type: assignment.relatable_type,
      relatable_id: assignment.relatable_id,
      company_product_id: assignment.company_product_id ?? '',
      company_product_phase_id:
        assignment.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProductPhase ? assignment.relatable_id : '',
    });
    if (assignment.company_product_id) {
      this.selectedCompanyProduct = {
        ...(this.selectedCompanyProduct ?? {}),
        id: assignment.company_product_id,
        title: assignment.company_product_title ?? this.selectedCompanyProduct?.title ?? '',
        color: assignment.company_product_color ?? this.selectedCompanyProduct?.color,
      } as CompanyProduct;
    } else {
      this.selectedCompanyProduct = undefined;
    }
    if (assignment.working_time_category) {
      this.selected_working_time_categories.set([assignment.working_time_category]);
      this.is_company_product_category =
        assignment.working_time_category.slug === 'company-product' ||
        assignment.working_time_category.parent_category_slug === 'company-product' ||
        assignment.relatable_id != null;
      this.is_company_product_phase_category =
        assignment.working_time_category.needs_relation_when_registering_time === 'company_product_phase';
    }
    this.form.markAsDirty();
    this.updateCompanyProductTripsCategoryState();
  }

  private patchSharedUserAssignment(index: number, assignment: CalendarEventParticipantCategoryAssignment): void {
    const value = this.sharedUsers();
    value[index] = {
      ...value[index],
      working_time_category_id: assignment.working_time_category_id,
      working_time_category: assignment.working_time_category,
      relatable_type: assignment.relatable_type,
      relatable_id: assignment.relatable_id,
      company_product_id: assignment.company_product_id ?? null,
      company_product_title: assignment.company_product_title,
      company_product_color: assignment.company_product_color,
      company_product_phase_name: assignment.company_product_phase_name,
    };
    this.form.controls['employee_calendar_register_user_shared_events'].patchValue(value);
    this.form.controls['employee_calendar_register_user_shared_events'].markAsDirty();
  }

  private firstConfiguredAssignment(): CalendarEventParticipantCategoryAssignment | null {
    const owner_assignment = this.ownerCategoryAssignment();
    if (owner_assignment.working_time_category_id != null) {
      return owner_assignment;
    }
    for (const shared_user of this.sharedUsers()) {
      const assignment = this.sharedUserToAssignment(shared_user);
      if (assignment.working_time_category_id != null) {
        return assignment;
      }
    }
    return null;
  }

  private async defaultAssignmentForUser(user: User): Promise<CalendarEventParticipantCategoryAssignment | null> {
    const assignment = this.firstConfiguredAssignment();
    if (assignment == null) {
      return null;
    }
    return (await this.userCanUseAssignment(user.id, assignment)) ? assignment : null;
  }

  private normalizedCompanyProductId(value: unknown): number | null {
    const product_id = Number(value);
    if (!Number.isFinite(product_id) || product_id <= 0) {
      return null;
    }
    return product_id;
  }

  private async userCanUseAssignment(user_id: number, assignment: CalendarEventParticipantCategoryAssignment): Promise<boolean> {
    if (assignment.working_time_category_id == null) {
      return false;
    }
    const company_product_id =
      this.normalizedCompanyProductId(assignment.company_product_id) ??
      (assignment.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProduct
        ? this.normalizedCompanyProductId(assignment.relatable_id)
        : null);
    if (company_product_id == null) {
      return true;
    }
    const members = await this.cacheProductMemberIds(company_product_id);
    return members.has(user_id);
  }

  private listenOnlineConferenceTypeInput() {
    this.form.controls['online_conference_type'].valueChanges.subscribe(value => {
      if (value !== 'external') {
        this.form.controls['call_link'].patchValue(null);
        this.form.controls['call_link'].clearValidators();
      } else if (value !== '') {
        this.form.controls['call_link'].setValidators([Validators.required, Validators.pattern(this.utils.urlRegex())]);
      }

      this.form.controls['call_link'].updateValueAndValidity();

      if (!this.form.value.is_blocked_for_meetings && value != '') {
        this.form.controls['is_blocked_for_meetings'].patchValue(true);
      }
    });
  }

  private listenStartInput() {
    // allarguem end date quan start sigui modificat
    this.start_date_ctrl.valueChanges.pipe(debounceTime(500)).subscribe(data => {
      // getRawValue: form.value omite controles disabled tras applyEditability()
      const form_value = this.form.getRawValue();
      const new_start_date: dayjs.Dayjs = form_value.all_day ? this.parseAllDayDateInMadrid(data) : dayjs(data);
      if (!isNaN(new_start_date.toDate().getTime())) {
        let new_end: dayjs.Dayjs | undefined;

        if (form_value.end != null && form_value.end !== '') {
          if (form_value.all_day) {
            const end_day = this.parseAllDayDateInMadrid(form_value.end);
            const old_start_day = this.parseAllDayDateInMadrid(this.lastFormValue.start);

            if (end_day.isValid() && old_start_day.isValid()) {
              const day_diff = end_day.startOf('day').diff(old_start_day.startOf('day'), 'day');
              new_end = new_start_date.startOf('day').add(day_diff, 'day');
            }
          } else {
            const end: Date = new Date(form_value.end);
            if (!isNaN(end.getTime())) {
              const old_start_date = new Date(this.lastFormValue.start);
              if (!isNaN(old_start_date.getTime())) {
                const diffInSeconds = dayjs(end).diff(dayjs(old_start_date), 'seconds');
                if (!isNaN(diffInSeconds)) {
                  new_end = dayjs(new_start_date).add(diffInSeconds, 'seconds');
                }
              }
            }
          }
        }

        const startValue = form_value.all_day
          ? new_start_date.format('YYYY-MM-DD')
          : this.datePipe.transform(new_start_date.toISOString(), 'yyyy-MM-ddTHH:mm:ss')!.slice(0, 16);

        const body: any = {
          start: startValue,
        };
        if (new_end)
          body.end = form_value.all_day
            ? new_end.tz(CalendarEventDetailDialogComponent.MADRID_TIMEZONE).format('YYYY-MM-DD')
            : this.datePipe.transform(new_end.toISOString(), 'yyyy-MM-ddTHH:mm:ss')!.slice(0, 16);
        else if (form_value.end == null || form_value.end === '') {
          const new_end_date = form_value.all_day ? new_start_date : new_start_date.add(1, 'hour');
          body.end = form_value.all_day
            ? new_end_date.tz(CalendarEventDetailDialogComponent.MADRID_TIMEZONE).format('YYYY-MM-DD')
            : this.datePipe.transform(new_end_date.toDate().toISOString(), 'yyyy-MM-ddTHH:mm:ss')!.slice(0, 16);
        }
        this.form.patchValue(body);
        // Update validation of end field when start changes
        this.form.controls['end'].updateValueAndValidity({ emitEvent: false });
        // Update validation of start field
        this.form.controls['start'].updateValueAndValidity({ emitEvent: false });
      }
    });

    // per fer accions interactives, posem un debounceTime
    this.form.controls['start'].valueChanges.subscribe(data => {
      if (data) {
        const start_of_week: Date = dayjs(data).startOf('week').toDate();
        if (!isNaN(start_of_week.getTime())) {
          const has_week_changed: boolean = !dayjs(this.old_start).isSame(dayjs(start_of_week), 'week');
          this.old_start = data;
          if (has_week_changed) {
            this.data.calendarComponent.onChangeWeekClicked(start_of_week);
          }
        }
        // Update validation of end field when start changes
        this.form.controls['end'].updateValueAndValidity({ emitEvent: false });
      }
    });

    // Update validation of start field when end changes
    this.form.controls['end'].valueChanges.subscribe(() => {
      if (!this.form.value.all_day) {
        this.form.controls['start'].updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  private listenEndInput() {
    this.form.controls['end'].valueChanges.pipe(debounceTime(100)).subscribe(end_value => {
      if (this.form.value.all_day && end_value != null && end_value !== '') {
        this.syncAllDayDatesFromEndSelection(end_value);
      }
    });
  }

  private syncAllDayDatesFromEndSelection(end_value: Date | string): void {
    const normalized_start = this.formatAllDayDateForForm(this.start_date_ctrl.value ?? this.form.value.start);
    let normalized_end = this.formatAllDayDateForForm(end_value);

    if (this.parseAllDayDateInMadrid(normalized_end).isBefore(this.parseAllDayDateInMadrid(normalized_start), 'day')) {
      normalized_end = normalized_start;
    }

    const current_end = this.formatAllDayDateForForm(this.form.value.end);
    const needs_sync = current_end !== normalized_end || this.form.value.start !== normalized_start;

    if (!needs_sync) {
      return;
    }

    this.form.patchValue({
      start: normalized_start,
      end: normalized_end,
    });

    this.start_date_ctrl.setValue(normalized_start, { emitEvent: false });
    this.form.controls['end'].updateValueAndValidity({ emitEvent: false });
    this.form.controls['start'].updateValueAndValidity({ emitEvent: false });
  }

  private initFilteredUsersToShare() {
    this.user_search_ctrl.valueChanges.pipe(debounceTime(500)).subscribe((data: string | User | null) => {
      if (data == null || typeof data !== 'string') {
        this.filtered_users_to_share = [];
        return;
      }

      const search = data.trim().toLowerCase();
      if (search.length >= 3) {
        this.api.getUsers({ search, active_workers_or_clients: 'true' }).subscribe(response => {
          this.filtered_users_to_share = response.data;
        });
      } else {
        this.filtered_users_to_share = [];
      }
    });
  }

  private listenFormChangesToTransmitToParentComponent() {
    this.form.valueChanges.pipe(debounceTime(100)).subscribe(data => {
      if (!this.shouldTransmitLivePreviewToCalendar()) {
        return;
      }

      const ecr: EmployeeCalendarRegisterWithRelationsSerialized = this.transformFormToEcrSerialized();
      if (ecr.from_date != null && ecr.to_date != null && ecr.working_time_category_id != null) {
        if (!isNaN(ecr.from_date.getTime()) && !isNaN(ecr.to_date.getTime())) {
          this.lastFormValue = data;
          this.data.calendarComponent.onSelectedEventModified(ecr);
        }
      }
    });
  }

  private shouldTransmitLivePreviewToCalendar(): boolean {
    return this.permissions_loaded() && this.can_edit() && this.isDialogFormDirty();
  }

  private isDialogFormDirty(): boolean {
    return (
      this.form.dirty ||
      this.start_date_ctrl.dirty ||
      this.team_event_ctrl.dirty ||
      this.recurrence_ctrl.dirty ||
      this.companyProductControl.dirty ||
      this.companyProductPhaseControl.dirty
    );
  }

  private markDialogFormAsPristineAfterInit(): void {
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.start_date_ctrl.markAsPristine();
    this.start_date_ctrl.markAsUntouched();
    this.team_event_ctrl.markAsPristine();
    this.team_event_ctrl.markAsUntouched();
    this.recurrence_ctrl.markAsPristine();
    this.recurrence_ctrl.markAsUntouched();
    this.companyProductControl.markAsPristine();
    this.companyProductControl.markAsUntouched();
    this.companyProductPhaseControl.markAsPristine();
    this.companyProductPhaseControl.markAsUntouched();
  }

  private tryMarkDialogFormAsPristineAfterHydration(part: 'permissions' | 'category_tree'): void {
    if (part === 'permissions') {
      this.permissions_hydration_done = true;
    } else {
      this.category_tree_hydration_done = true;
    }

    if (!this.permissions_hydration_done || !this.category_tree_hydration_done) {
      return;
    }

    // Do not wipe user edits that happened while async hydration was still running
    if (this.isDialogFormDirty() || this.notificationsChanged) {
      return;
    }

    this.markDialogFormAsPristineAfterInit();
  }

  private transformFormToEcrSerialized(): EmployeeCalendarRegisterWithRelationsSerialized {
    const form_value = this.form.getRawValue();
    const selected_categories = this.selected_working_time_categories();
    const original_ecr = this.event?.meta?.ecr;
    const wtc =
      selected_categories[selected_categories.length - 1] ?? form_value.working_time_category ?? original_ecr?.working_time_category;

    // Prefer the explicit relatable fields captured during selection flow
    const relatable_type_from_form = form_value.relatable_type;
    const relatable_id_from_form = form_value.relatable_id;

    // Fallback derivation if not present in form
    const fallback_relatable_type =
      wtc?.needs_relation_when_registering_time === 'company_product_phase'
        ? EmployeeCalendarRegisterRelatableType.CompanyProductPhase
        : this.is_company_product_category
          ? EmployeeCalendarRegisterRelatableType.CompanyProduct
          : original_ecr?.relatable_type;
    const fallback_relatable_id =
      wtc?.needs_relation_when_registering_time === 'company_product_phase'
        ? form_value.company_product_phase_id
        : this.is_company_product_category
          ? form_value.company_product_id
          : original_ecr?.relatable_id;

    const finalRelatableType = relatable_type_from_form ?? fallback_relatable_type;
    const finalRelatableId = relatable_id_from_form ?? fallback_relatable_id;

    // Get company_product color and title from selected company_product, with original ECR fallbacks for preview
    let company_product_color: string | undefined = this.selectedCompanyProduct?.color ?? original_ecr?.company_product_color ?? undefined;
    let company_product_title: string | undefined = this.selectedCompanyProduct?.title ?? original_ecr?.company_product_title ?? undefined;
    let company_product_phase_name: string | undefined =
      this.selectedCompanyProductPhase?.company_product_phase_type?.name ?? original_ecr?.company_product_phase_name ?? undefined;

    if (finalRelatableType === EmployeeCalendarRegisterRelatableType.CompanyProduct && this.selectedCompanyProduct) {
      company_product_color = this.selectedCompanyProduct.color ?? company_product_color;
      company_product_title = this.selectedCompanyProduct.title ?? company_product_title;
    } else if (finalRelatableType === EmployeeCalendarRegisterRelatableType.CompanyProductPhase) {
      company_product_phase_name = this.selectedCompanyProductPhase?.company_product_phase_type?.name ?? company_product_phase_name;
      if (this.selectedCompanyProduct) {
        company_product_color = this.selectedCompanyProduct.color ?? company_product_color;
        company_product_title = this.selectedCompanyProduct.title ?? company_product_title;
      }
    }

    const wtc_name = wtc?.name ?? original_ecr?.wtc_name ?? undefined;
    const wtc_color = wtc?.color ?? original_ecr?.wtc_color ?? undefined;
    const wtc_slug = wtc?.slug ?? original_ecr?.wtc_slug ?? undefined;

    return {
      id: form_value.id,
      all_day: form_value.all_day,
      from_date: form_value.all_day ? this.toAllDayBoundaryDate(form_value.start, 'start') : new Date(form_value.start),
      to_date: form_value.all_day ? this.toAllDayBoundaryDate(form_value.end, 'end') : new Date(form_value.end),
      user_id: this.resolveSerializedUserId(),
      custom_title: form_value.custom_title,
      description: form_value.description,
      call_link: form_value.call_link,
      meet_mode: form_value.meet_mode,
      online_conference_type: form_value.online_conference_type,
      online_conference_id: this.data.event?.meta.ecr.online_conference_id,
      is_blocked_for_meetings: form_value.is_blocked_for_meetings,

      // recurrence
      parent_recurrent_employee_calendar_register_id: form_value.parent_recurrent_employee_calendar_register_id,
      recurrence_freq: form_value.recurrence_freq,
      recurrence_interval: form_value.recurrence_freq ? (form_value.recurrence_interval ?? 1) : form_value.recurrence_interval,
      recurrence_ending_count: form_value.recurrence_ending_count,
      recurrence_ending_until: form_value.recurrence_ending_until,
      recurrence_byday: form_value.recurrence_byday,
      recurrence_bymonthday: form_value.recurrence_bymonthday,
      recurrence_byyearday: form_value.recurrence_byyearday,
      recurrence_byweekno: form_value.recurrence_byweekno,
      recurrence_bymonth: form_value.recurrence_bymonth,
      recurrence_bysetpos: form_value.recurrence_bysetpos,

      // altres
      employee_calendar_register_user_shared_events: this.serializeSharedUsersForPersistence(
        form_value.employee_calendar_register_user_shared_events
      ),
      employee_calendar_register_notification_requests: [
        ...this.notifications.map(n => ({
          id: n.id || undefined,
          user_id: n.user_id,
          minutes_before: n.minutes_before,
          channels: n.channels,
          _delete: false,
        })),
        ...this.deletedNotificationIds.map((id: number) => ({
          id,
          _delete: true,
        })),
      ],

      working_time_category: wtc,
      working_time_category_id: wtc?.id ?? original_ecr?.working_time_category_id,
      wtc_slug: wtc_slug,
      wtc_color: wtc_color,
      wtc_name: wtc_name,
      relatable_type: finalRelatableType || null,
      relatable_id: finalRelatableId === '' || finalRelatableId == null ? null : Number(finalRelatableId),
      origin_location_id: this.should_show_location_selectors ? this.form.value.origin_location_id : null,
      destination_location_id: this.should_show_location_selectors ? this.form.value.destination_location_id : null,

      // CompanyProduct-related fields for color and title
      company_product_color: company_product_color,
      company_product_title: company_product_title,
      company_product_phase_name: company_product_phase_name,
    } as any;
  }

  private serializeSharedUsersForPersistence(
    shared_users: EmployeeCalendarRegisterUserSharedEvent[] | undefined
  ): EmployeeCalendarRegisterUserSharedEvent[] {
    return (shared_users ?? []).map(shared_user => ({
      ...shared_user,
      user_id: shared_user.user_id ?? undefined,
      anonymous_user_email: shared_user.anonymous_user_email || undefined,
      working_time_category_id: shared_user.working_time_category_id ?? null,
      relatable_type: shared_user.relatable_type || null,
      relatable_id: shared_user.relatable_id ? Number(shared_user.relatable_id) : null,
    }));
  }

  private listenSharedUsersInput() {
    this.form.controls['employee_calendar_register_user_shared_events'].valueChanges.subscribe(
      (value: EmployeeCalendarRegisterUserSharedEvent[] | undefined) => {
        if (value) {
          // gestionar is_blocked_for_meetings
          if (!this.form.value.is_blocked_for_meetings && this.form.controls['is_blocked_for_meetings'].untouched && value.length > 0) {
            this.form.controls['is_blocked_for_meetings'].patchValue(true);
          }

          // gestionar availability calendar
          for (const ecruse of value) {
            void this.data.calendarComponent.enableAvailabilityPreviewForUser(ecruse.user!);
          }

          const current_users: User[] = value.map(ecruse => ecruse.user!);
          for (const row of this.data.calendarComponent.availability_compatibility_users) {
            // si hi ha un usuari que s'ha desmarcat
            if (current_users.find(u => u.id == row.user.id) === undefined) {
              this.data.calendarComponent.disableAvailabilityViewForUser(row.user);
            }
          }
        }
      }
    );
  }

  private transformWorkingCategorySerializedToModelized(wtc: WorkingTimeCategory): WorkingTimeCategory {
    return wtc;
  }

  private listenAllDayInput() {
    this.form.controls['all_day'].valueChanges.subscribe((all_day: boolean) => {
      if (all_day) {
        const start_day = this.parseAllDayDateInMadrid(this.form.value.start);
        const valid_start_day = start_day.isValid()
          ? start_day.startOf('day')
          : dayjs.tz(undefined, CalendarEventDetailDialogComponent.MADRID_TIMEZONE).startOf('day');
        const end_day =
          this.form.value.end && this.parseAllDayDateInMadrid(this.form.value.end).isValid()
            ? this.parseAllDayDateInMadrid(this.form.value.end).startOf('day')
            : valid_start_day;
        const start_day_val = valid_start_day.format('YYYY-MM-DD');
        const end_day_val = end_day.format('YYYY-MM-DD');
        const body: any = {
          start: start_day_val,
          end: end_day_val,
        };
        this.start_date_ctrl.setValue(start_day_val, { emitEvent: false });
        this.form.patchValue(body);
        this.form.controls['end'].updateValueAndValidity({ emitEvent: false });
        this.form.controls['start'].updateValueAndValidity({ emitEvent: false });
      } else {
        const start_day_val = dayjs(this.start_date_ctrl.value).hour(10);
        const end_day_val = start_day_val.clone().add(1, 'hour');
        this.start_date_ctrl.setValue(this.datePipe.transform(start_day_val.toDate().toISOString(), 'yyyy-MM-ddTHH:mm:ss')!.slice(0, 16), {
          emitEvent: false,
        });
        const body: any = {
          start: this.start_date_ctrl.value,
          end: this.datePipe.transform(end_day_val.toDate().toISOString(), 'yyyy-MM-ddTHH:mm:ss')!.slice(0, 16),
        };
        this.form.patchValue(body);
      }
    });
  }

  private listenTeamEventCtrl() {
    this.team_event_ctrl.valueChanges.subscribe((is_team_event: boolean) => {
      const next_user_id = is_team_event ? null : (this.original_event_user_id ?? this.me.id);
      this.form.controls['user_id'].patchValue(next_user_id);
      this.syncOwnershipFromFormUserId(next_user_id);
      this.form.markAsDirty();
      if (is_team_event) {
        this.form.controls['employee_calendar_register_user_shared_events'].patchValue([]);
        this.form.controls['employee_calendar_register_user_shared_events'].markAsDirty();
      }
    });
  }

  private initOriginalEventOwnership(): void {
    const owner_user_id = this.event?.meta.ecr?.user_id;
    const has_owner = owner_user_id != null;
    this.original_event_user_id = has_owner ? +owner_user_id : null;
    this.has_original_event_owner_user.set(has_owner);
    this.is_original_event_owner.set(!has_owner || owner_user_id == this.me.id);
    this.owner_display_name.set([this.event?.meta.ecr?.user_name, this.event?.meta.ecr?.user_surnames].filter(Boolean).join(' ').trim());
  }

  private resolveSerializedUserId(): number | null {
    const next_user_id = this.normalizeFormUserId(this.form.getRawValue().user_id);
    const is_new_event = resolvePersistedEmployeeCalendarRegisterId(this.event) === null;

    if (is_new_event) {
      return next_user_id;
    }

    if (isAllowedEmployeeCalendarRegisterUserIdPayloadChange(this.original_event_user_id, next_user_id, this.me.id)) {
      return next_user_id === null ? null : this.me.id;
    }

    return this.original_event_user_id;
  }

  private normalizeFormUserId(user_id: number | string | null | undefined): number | null {
    if (user_id == null || user_id === '') {
      return null;
    }

    return +user_id;
  }

  private syncOwnershipFromFormUserId(user_id: number | string | null | undefined): void {
    this.is_owner.set(user_id == null || user_id === '' || user_id == this.me.id);
  }

  private initMyEventPermissions(): void {
    const persisted_ecr_id = resolvePersistedEmployeeCalendarRegisterId(this.event);
    this.is_persisted_event.set(persisted_ecr_id !== null);

    if (persisted_ecr_id === null) {
      this.can_edit.set(true);
      this.permissions_loaded.set(true);
      this.applyEditability(true);
      this.tryMarkDialogFormAsPristineAfterHydration('permissions');
      return;
    }

    this.api
      .getEmployeeCalendarRegisterCanIEdit(persisted_ecr_id)
      .pipe(
        catchError(() => of({ can_edit: false })),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(permissions => {
        this.can_edit.set(permissions.can_edit);
        this.permissions_loaded.set(true);
        this.applyEditability(permissions.can_edit);
        this.tryMarkDialogFormAsPristineAfterHydration('permissions');
      });
  }

  private applyEditability(can_edit: boolean): void {
    const category_editable = this.event?.meta.ecr.working_time_category?.is_editable_from_calendar ?? true;
    const next_updatable = can_edit && this.data.updatable && category_editable;
    this.updatable.set(next_updatable);
    const emit_opts = { emitEvent: false };

    if (!next_updatable) {
      this.form.disable(emit_opts);
      this.start_date_ctrl.disable(emit_opts);
      this.team_event_ctrl.disable(emit_opts);
      this.user_search_ctrl.disable(emit_opts);
      this.recurrence_ctrl.disable(emit_opts);
      this.companyProductControl.disable(emit_opts);
      this.companyProductPhaseControl.disable(emit_opts);
      return;
    }

    this.form.enable(emit_opts);
    this.start_date_ctrl.enable(emit_opts);
    this.team_event_ctrl.enable(emit_opts);
    this.user_search_ctrl.enable(emit_opts);
    this.companyProductControl.enable(emit_opts);
    this.companyProductPhaseControl.enable(emit_opts);

    if (!this.is_owner() && this.event?.meta.ecr.recurrence_freq != null) {
      this.recurrence_ctrl.disable(emit_opts);
    } else {
      this.recurrence_ctrl.enable(emit_opts);
    }
  }

  private listenRecurrentCtrl() {
    this.recurrence_ctrl.valueChanges.subscribe(val => {
      if (['daily', 'weekly', 'biweekly', 'byweekno', 'byyearday', 'laboralday', 'bymonthday'].includes(val)) {
        if (val === 'daily') {
          this.form.patchValue(this.DAILY_FORM_CONFIG);
        } else if (val === 'weekly') {
          this.form.patchValue(this.WEEKLY_FORM_CONFIG);
        } else if (val === 'biweekly') {
          this.form.patchValue(this.BIWEEKLY_FORM_CONFIG);
        } else if (val === 'byweekno') {
          this.form.patchValue(this.BYWEEKNO_FORM_CONFIG);
        } else if (val === 'bymonthday') {
          this.form.patchValue(this.BYMONTH_FORM_CONFIG);
        } else if (val === 'byyearday') {
          this.form.patchValue(this.BYYEARDAY_FORM_CONFIG);
        } else if (val === 'laboralday') {
          this.form.patchValue(this.LABORALDAY_FORM_CONFIG);
        } else throw 'WTF is that status';

        this.customRecurrenceText = undefined;
      } else if (val === 'customize') {
        this.openAndHandleRecurrenceCustomizationPopup();
      } else if (val === 'customized') {
        this.initCustomTextRecurrence();
      } else if (val === '') {
        this.form.patchValue(this.EMPTY_RECURRENCE_FORM_CONFIG);
      } else throw 'WTF is that status';
    });
  }

  private transformEcrToRecurrenceCtrlValue():
    | ''
    | 'customized'
    | 'daily'
    | 'weekly'
    | 'biweekly'
    | 'bymonthday'
    | 'byweekno'
    | 'byyearday'
    | 'laboralday'
    | 'customize' {
    if (!this.eventHasRecurrenceAttributes()) return '';
    else if (this.isSameFormConfigAs(this.DAILY_FORM_CONFIG)) return 'daily';
    else if (this.isSameFormConfigAs(this.WEEKLY_FORM_CONFIG)) return 'weekly';
    else if (this.isSameFormConfigAs(this.BIWEEKLY_FORM_CONFIG)) return 'biweekly';
    else if (this.isSameFormConfigAs(this.BYWEEKNO_FORM_CONFIG)) return 'byweekno';
    else if (this.isSameFormConfigAs(this.BYMONTH_FORM_CONFIG)) return 'bymonthday';
    else if (this.isSameFormConfigAs(this.BYYEARDAY_FORM_CONFIG)) return 'byyearday';
    else if (this.isSameFormConfigAs(this.LABORALDAY_FORM_CONFIG)) return 'laboralday';
    else {
      return 'customized';
    }
  }

  private initFormConfigDefaults() {
    this.DAILY_FORM_CONFIG = {
      recurrence_freq: 'daily',
      recurrence_interval: undefined,
      recurrence_ending_count: undefined,
      recurrence_ending_until: undefined,
      recurrence_byday: undefined,
      recurrence_bymonthday: undefined,
      recurrence_byyearday: undefined,
      recurrence_byweekno: undefined,
      recurrence_bymonth: undefined,
      recurrence_bysetpos: undefined,
    };
    this.WEEKLY_FORM_CONFIG = {
      recurrence_freq: 'weekly',
      recurrence_interval: undefined,
      recurrence_ending_count: undefined,
      recurrence_ending_until: undefined,
      recurrence_byday: [dayjs(this.form.value.start).isoWeekday()],
      recurrence_bymonthday: undefined,
      recurrence_byyearday: undefined,
      recurrence_byweekno: undefined,
      recurrence_bymonth: undefined,
      recurrence_bysetpos: undefined,
    };
    this.BIWEEKLY_FORM_CONFIG = {
      recurrence_freq: 'weekly',
      recurrence_interval: 2,
      recurrence_ending_count: undefined,
      recurrence_ending_until: undefined,
      recurrence_byday: [dayjs(this.form.value.start).isoWeekday()],
      recurrence_bymonthday: undefined,
      recurrence_byyearday: undefined,
      recurrence_byweekno: undefined,
      recurrence_bymonth: undefined,
      recurrence_bysetpos: undefined,
    };
    this.BYWEEKNO_FORM_CONFIG = {
      recurrence_freq: 'monthly',
      recurrence_interval: undefined,
      recurrence_ending_count: undefined,
      recurrence_ending_until: undefined,
      recurrence_byday: [dayjs(this.form.value.start).isoWeekday()],
      recurrence_bymonthday: undefined,
      recurrence_byyearday: undefined,
      recurrence_byweekno: undefined,
      recurrence_bymonth: undefined,
      recurrence_bysetpos: [this.utils.getWeekNumberOfMonth(this.form.value.start)],
    };
    this.BYMONTH_FORM_CONFIG = {
      recurrence_freq: 'monthly',
      recurrence_interval: undefined,
      recurrence_ending_count: undefined,
      recurrence_ending_until: undefined,
      recurrence_byday: undefined,
      recurrence_bymonthday: [dayjs(this.form.value.start).date()],
      recurrence_byyearday: undefined,
      recurrence_byweekno: undefined,
      recurrence_bymonth: undefined,
      recurrence_bysetpos: undefined,
    };
    this.BYYEARDAY_FORM_CONFIG = {
      recurrence_freq: 'yearly',
      recurrence_interval: undefined,
      recurrence_ending_count: undefined,
      recurrence_ending_until: undefined,
      recurrence_byday: undefined,
      recurrence_bymonthday: undefined,
      recurrence_byyearday: [dayjs(this.form.value.start).dayOfYear()],
      recurrence_byweekno: undefined,
      recurrence_bymonth: undefined,
      recurrence_bysetpos: undefined,
    };
    this.LABORALDAY_FORM_CONFIG = {
      recurrence_freq: 'weekly',
      recurrence_interval: undefined,
      recurrence_ending_count: undefined,
      recurrence_ending_until: undefined,
      recurrence_byday: [1, 2, 3, 4, 5],
      recurrence_bymonthday: undefined,
      recurrence_byyearday: undefined,
      recurrence_byweekno: undefined,
      recurrence_bymonth: undefined,
      recurrence_bysetpos: undefined,
    };
    this.EMPTY_RECURRENCE_FORM_CONFIG = {
      recurrence_freq: undefined,
      recurrence_interval: undefined,
      recurrence_ending_count: undefined,
      recurrence_ending_until: undefined,
      recurrence_byday: undefined,
      recurrence_bymonthday: undefined,
      recurrence_byyearday: undefined,
      recurrence_byweekno: undefined,
      recurrence_bymonth: undefined,
      recurrence_bysetpos: undefined,
    };
  }

  private eventHasRecurrenceAttributes(): boolean {
    return this.form.controls['recurrence_freq'].value != null;
  }

  private isSameFormConfigAs(config: EmployeeCalendarRegisterRecurrenceAttributesConfig): boolean {
    for (const form_key of Object.keys(config) as (keyof EmployeeCalendarRegisterRecurrenceAttributesConfig)[]) {
      if (Array.isArray(config[form_key]) && this.form.controls[form_key].value != null) {
        if (!this.utils.arraysAreEqual(this.form.controls[form_key].value.sort(), (config[form_key] as any[]).sort())) return false;
      } else {
        if (this.form.controls[form_key].value != config[form_key]) return false;
      }
    }
    return true;
  }

  private initCustomTextRecurrence(): void {
    const form = this.form.controls; // Asegúrate de que tu formulario esté definido
    let recurrenceText = '';

    const freqMap: { [key: string]: { singular: string; plural: string } } = {
      daily: { singular: 'día', plural: 'días' },
      weekly: { singular: 'semana', plural: 'semanas' },
      monthly: { singular: 'mes', plural: 'meses' },
      yearly: { singular: 'año', plural: 'años' },
    };
    const daysMap: { [key: string]: string } = {
      1: 'lunes',
      2: 'martes',
      3: 'miércoles',
      4: 'jueves',
      5: 'viernes',
      6: 'sábado',
      7: 'domingo',
    };

    // Frecuencia
    const freq = form['recurrence_freq'].value;
    const interval = form['recurrence_interval'].value || 1;
    if (freq) {
      recurrenceText = `Cada ${interval > 1 ? `${interval} ${freqMap[freq].plural}` : freqMap[freq].singular}`;
    }

    // Día(s) de la semana
    const byDayRaw = form['recurrence_byday'].value;
    const byDay = Array.isArray(byDayRaw) ? byDayRaw : [];
    const hasByDay = byDay.length > 0;
    const bySetPosRaw = form['recurrence_bysetpos'].value;
    const bySetPos = Array.isArray(bySetPosRaw) ? bySetPosRaw : [];
    const hasBySetPos = bySetPos.length > 0;

    if (hasByDay && !(freq === 'monthly' && hasBySetPos)) {
      const daysText = byDay
        .sort()
        .map((day: number) => daysMap[day])
        .join(', ');
      recurrenceText += `, el ${daysText}`;
    }

    // Día del mes
    const byMonthDay = form['recurrence_bymonthday'].value;
    if (byMonthDay) {
      recurrenceText += `, el día ${byMonthDay}`;
    }

    // Semana del año
    const byWeekNo = form['recurrence_byweekno'].value;
    if (byWeekNo) {
      recurrenceText += `, en la semana ${byWeekNo}`;
    }

    // Día del año
    const byYearDay = form['recurrence_byyearday'].value;
    if (byYearDay) {
      recurrenceText += `, el día ${byYearDay} del año`;
    }

    // Mes
    const byMonth = form['recurrence_bymonth'].value;
    if (byMonth) {
      const monthsMap: { [key: number]: string } = {
        1: 'enero',
        2: 'febrero',
        3: 'marzo',
        4: 'abril',
        5: 'mayo',
        6: 'junio',
        7: 'julio',
        8: 'agosto',
        9: 'septiembre',
        10: 'octubre',
        11: 'noviembre',
        12: 'diciembre',
      };
      recurrenceText += `, en ${monthsMap[byMonth]}`;
    }

    // Posición en el mes (e.g., "el cuarto martes")
    if (hasBySetPos) {
      const positionMap: { [key: number]: string } = {
        1: 'primer',
        2: 'segundo',
        3: 'tercer',
        4: 'cuarto',
        5: 'quinto',
        [-3]: 'antepenúltimo',
        [-4]: 'cuarto por el final',
        [-5]: 'quinto por el final',
        [-1]: 'último',
        [-2]: 'penúltimo',
      };
      if (hasByDay) {
        const firstBySetPos = bySetPos[0];
        const firstByDay = byDay[0];
        const dayText = daysMap[firstByDay] ?? firstByDay;
        const positionText = positionMap[firstBySetPos] ?? firstBySetPos;
        recurrenceText += `, el ${positionText} ${dayText}`;
      }
    }

    // Finalización
    const endingUntil = form['recurrence_ending_until'].value;
    const endingCount = form['recurrence_ending_count'].value;
    if (endingUntil) {
      recurrenceText += `, hasta el ${new Date(endingUntil).toLocaleDateString()}`;
    } else if (endingCount) {
      recurrenceText += `, hasta que ocurra ${endingCount} veces`;
    }

    this.customRecurrenceText = recurrenceText || 'No se ha configurado una recurrencia válida';
  }

  private openAndHandleRecurrenceCustomizationPopup() {
    const dialogRef = this.dialog.open(CalendarEventDetailRecurrenceCustomizationDialogComponent, {
      width: '350px',
      data: {
        recurrence_configuration: this.transformFormToRecurrenceConfiguration(),
        current_day: dayjs(this.form.value.start).toDate(),
      },
    });

    dialogRef.afterClosed().subscribe((data: EmployeeCalendarRegisterRecurrenceAttributesConfig | undefined) => {
      if (data) {
        this.form.patchValue(data);
        this.recurrence_ctrl.patchValue('customized');
      } else {
        this.recurrence_ctrl.patchValue(this.transformEcrToRecurrenceCtrlValue());
      }
    });
  }

  private transformFormToRecurrenceConfiguration(): EmployeeCalendarRegisterRecurrenceAttributesConfig {
    return {
      recurrence_freq: this.form.value.recurrence_freq,
      recurrence_interval: this.form.value.recurrence_interval,
      recurrence_ending_count: this.form.value.recurrence_ending_count,
      recurrence_ending_until: this.form.value.recurrence_ending_until,
      recurrence_byday: this.form.value.recurrence_byday,
      recurrence_bymonthday: this.form.value.recurrence_bymonthday,
      recurrence_byyearday: this.form.value.recurrence_byyearday,
      recurrence_byweekno: this.form.value.recurrence_byweekno,
      recurrence_bymonth: this.form.value.recurrence_bymonth,
      recurrence_bysetpos: this.form.value.recurrence_bysetpos,
    };
  }

  private isCreate() {
    return typeof this.form.value.id === 'string' && this.form.value.id.startsWith('tmp_');
  }

  private formatAllDayDateForForm(value: Date | string): string {
    return dayjs(value).tz(CalendarEventDetailDialogComponent.MADRID_TIMEZONE).format('YYYY-MM-DD');
  }

  private parseAllDayDateInMadrid(value: Date | string): dayjs.Dayjs {
    return dayjs.tz(value, CalendarEventDetailDialogComponent.MADRID_TIMEZONE);
  }

  private getEventDatesFromForm(): { from_date: Date; to_date: Date; id?: number; all_day: boolean } | null {
    const start = this.form.value.start;
    const end = this.form.value.end;

    if (start == null || start === '' || end == null || end === '') {
      return null;
    }

    const from_date = this.form.value.all_day ? this.toAllDayBoundaryDate(start, 'start') : new Date(start);
    const to_date = this.form.value.all_day ? this.toAllDayBoundaryDate(end, 'end') : new Date(end);

    if (isNaN(from_date.getTime()) || isNaN(to_date.getTime())) {
      return null;
    }

    return { from_date, to_date, id: this.form.value.id, all_day: !!this.form.value.all_day };
  }

  private toAllDayBoundaryDate(value: Date | string, boundary: 'start' | 'end'): Date {
    return new Date(this.utils.normalizeMadridDateForApi(value, boundary));
  }

  private listenMeetingModeCompanyProduct() {
    this.employee.meetingModeCompanyProduct.subscribe(result => {
      this.meeting_mode_company_product = result;
    });
  }

  private fetchWorkingTimeCategories(company_product_id: string = '') {
    const params: any = {};
    if (company_product_id != '') params.company_product_id = company_product_id;

    // Wait for both API calls to complete before loading the category tree
    forkJoin({
      assignable: this.api.getUserAssignableWorkingTimeCategories(this.form.getRawValue()['user_id'], params),
      all: this.api.getAllWorkingTimeCategories(params),
    }).subscribe(({ assignable, all }) => {
      this.working_time_categories = assignable.filter(c => c.parent_category_slug == null);
      this.all_working_time_categories = all;
      this.registerWorkingTimeCategories([...assignable, ...all]);

      // Load category tree after both arrays are available
      if (this.data.event != null && this.data.event.meta.ecr != null && this.data.event.meta.ecr.working_time_category_id) {
        // Combine both arrays to ensure we can find the category even if it's not assignable
        const allCategories = [...assignable, ...all.filter(c => !assignable.find(a => a.id === c.id))];
        void this.loadWorkingTimeCategoryTree(this.data.event.meta.ecr, allCategories);
      } else {
        this.tryMarkDialogFormAsPristineAfterHydration('category_tree');
      }
    });
  }

  private async loadWorkingTimeCategoryTree(
    ecr: EmployeeCalendarRegisterWithRelationsSerialized,
    working_time_categories: WorkingTimeCategory[]
  ): Promise<void> {
    this.is_hydrating_category_tree = true;

    try {
      const tree: WorkingTimeCategory[] = [];

      const company_product_id = ecr.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProduct ? ecr.relatable_id : null;
      let company_product: CompanyProduct | undefined;
      if (company_product_id) {
        company_product = await lastValueFrom(this.api.getCompanyProduct(company_product_id!.toString()));
      }

      const company_product_phase_id =
        ecr.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProductPhase ? ecr.relatable_id : null;
      let company_product_phase: CompanyProductPhase | undefined;
      if (company_product_phase_id) {
        company_product_phase = await lastValueFrom(this.api.getCompanyProductPhase(company_product_phase_id!));
        company_product = await lastValueFrom(this.api.getCompanyProduct(company_product_phase.company_product_id!.toString()));
      }

      let category = working_time_categories.find(c => c.id == ecr.working_time_category_id);
      if (category == null) {
        console.warn('Category not found for working_time_category_id:', ecr.working_time_category_id);
        return;
      }

      tree.unshift(category);
      while (category!.parent_category_slug != null) {
        const parent_slug: string = category!.parent_category_slug;
        const parent_category = working_time_categories.find(c => c.slug == parent_slug);
        if (parent_category == null) {
          console.warn('Parent category not found for slug:', parent_slug);
          break;
        }
        category = parent_category;
        tree.unshift(category);
      }

      this.registerWorkingTimeCategories(tree);
      this.selected_working_time_categories.set([]);
      this.clearAvailableWorkingTimeCategoriesFromLevel(0);

      // Set product/phase before loading product-scoped children so requests include company_product_id
      if (company_product) {
        this.selectedCompanyProduct = company_product;
        this.form.patchValue(
          {
            company_product_id: company_product.id,
            relatable_id: company_product_phase_id != null ? company_product_phase_id : company_product.id,
            relatable_type:
              company_product_phase_id != null
                ? EmployeeCalendarRegisterRelatableType.CompanyProductPhase
                : EmployeeCalendarRegisterRelatableType.CompanyProduct,
          },
          { emitEvent: false }
        );
        this.companyProductControl.setValue(company_product, { emitEvent: false });
        void this.cacheProductMemberIds(company_product.id);
      }

      if (company_product_phase) {
        this.selectedCompanyProductPhase = company_product_phase;
        this.form.patchValue({ company_product_phase_id: company_product_phase.id }, { emitEvent: false });
        this.companyProductPhaseControl.setValue(company_product_phase, { emitEvent: false });
      }

      const root_category = tree[0];
      this.is_company_product_category = root_category?.slug === 'company-product';

      const selected_categories: WorkingTimeCategory[] = [];

      for (let level = 0; level < tree.length; level++) {
        const tree_category = tree[level];
        selected_categories.push(tree_category);
        this.selected_working_time_categories.set([...selected_categories]);

        if (tree_category.parent_category_slug == null) {
          this.is_company_product_category = tree_category.slug === 'company-product';
        }
        if (tree_category.needs_relation_when_registering_time === 'company_product_phase') {
          this.is_company_product_phase_category = true;
        }

        this.form.patchValue(
          {
            [`working_time_category_level_${level}`]: tree_category.id,
            working_time_category_id: tree_category.id,
            working_time_category: tree_category,
          },
          { emitEvent: false }
        );

        const children = await this.loadWorkingTimeCategoryChildrenForHydration(tree_category.id.toString());
        if (children.length > 0) {
          const next_category = tree[level + 1];
          const options =
            next_category != null && !children.some(child => child.id === next_category.id) ? [...children, next_category] : children;
          this.seedAvailableWorkingTimeCategories(level, tree_category.id.toString(), options);
        }
      }

      if (company_product_phase) {
        this.form.patchValue(
          {
            company_product_phase_id: company_product_phase.id,
            relatable_id: company_product_phase.id,
            relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProductPhase,
          },
          { emitEvent: false }
        );
      }

      // Como que no guardamos el estado en BBDD, lo calculamos
      this.is_travel_time_checked = (ecr.origin_location_id ?? null) !== null && (ecr.destination_location_id ?? null) !== null;
      this.shows_travel_time_checkbox = !this.is_company_product_trips_category && !this.is_company_product_category;
      this.updateCompanyProductTripsCategoryState();
      this.patchLocationIdsFromEcr(ecr);
      this.syncTripState();
    } finally {
      this.is_hydrating_category_tree = false;
      this.tryMarkDialogFormAsPristineAfterHydration('category_tree');
    }
  }

  private async loadWorkingTimeCategoryChildrenForHydration(category_id: string): Promise<WorkingTimeCategory[]> {
    try {
      const children = await lastValueFrom(
        this.api.getWorkingTimeCategoryChildren(category_id, {
          company_product_id: this.form.getRawValue()['company_product_id'],
        })
      );
      this.registerWorkingTimeCategories(children);
      return children;
    } catch {
      return [];
    }
  }

  private registerWorkingTimeCategories(categories: WorkingTimeCategory[]): void {
    for (const category of categories) {
      this.working_time_category_lookup.set(category.id.toString(), category);
    }
  }

  private seedAvailableWorkingTimeCategories(level: number, parent_id: string, categories: WorkingTimeCategory[]): void {
    this.registerWorkingTimeCategories(categories);
    this.working_time_category_children_changes.next({
      type: 'seed',
      id: parent_id,
      level,
      categories,
    });
  }

  private toLocationId(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return Number(value);
  }

  private patchLocationIdsFromEcr(ecr: EmployeeCalendarRegisterWithRelationsSerialized): void {
    if (!this.is_company_product_trips_category && !this.is_travel_time_checked) {
      return;
    }

    this.form.patchValue(
      {
        origin_location_id: this.toLocationId(ecr.origin_location_id),
        destination_location_id: this.toLocationId(ecr.destination_location_id),
      },
      { emitEvent: false }
    );
  }

  onCategoryChange(event: WorkingTimeCategorySelectionChange | null, level: number): void {
    if (event == null || event.value == null) {
      return;
    }

    // Clear any existing selections and available subcategories from the current level onwards
    this.clearAvailableWorkingTimeCategoriesFromLevel(level);

    const category = this.getWorkingTimeCategoryById(event.value.toString());
    const previous_is_company_product_category = this.is_company_product_category;
    const previous_is_company_product_phase_category = this.is_company_product_phase_category;
    if (category != null) {
      if (category.parent_category_slug == null) {
        this.is_company_product_category = category.slug == 'company-product';
      } else if (level == 1) {
        this.is_company_product_phase_category = category.needs_relation_when_registering_time == 'company_product_phase';
        if (!this.is_company_product_phase_category) {
          this.form.patchValue({
            relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProduct,
            relatable_id: this.form.value['company_product_id'],
          });
        } else {
          this.autoSelectCompanyProductPhase();
        }
      }
      this.is_company_product_phase_category = category.needs_relation_when_registering_time == 'company_product_phase';
    }

    // If we moved away from a company-product-based category, clear company_product and phase selections
    if (previous_is_company_product_category && !this.is_company_product_category) {
      this.form.patchValue({ company_product_id: '', company_product_phase_id: '', relatable_type: undefined, relatable_id: undefined });
      this.companyProductControl.setValue('');
      this.companyProductPhaseControl.setValue('');
      this.selectedCompanyProduct = undefined;
      this.selectedCompanyProductPhase = undefined;
    }

    // If we are within company_product flow but no longer need company_product phase, clear phase
    if (this.is_company_product_category && !this.is_company_product_phase_category && previous_is_company_product_phase_category) {
      this.form.patchValue({
        company_product_phase_id: '',
        relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProduct,
        relatable_id: this.form.value['company_product_id'],
      });
      this.companyProductPhaseControl.setValue('');
    }

    if (category == null) {
      this.selected_working_time_categories.update(categories => categories.slice(0, level));
      return;
    }

    this.registerWorkingTimeCategories([category]);
    this.selected_working_time_categories.update(categories => [...categories.slice(0, level), category]);

    // Update the working_time_category field with the final selected category
    // This is needed for the Validators.required on the working_time_category field
    this.form.patchValue({
      working_time_category: category,
      working_time_category_id: category.id,
    });

    if (category.slug != 'company-product') {
      this.fetchWorkingTimeCategoryChildren(event.value.toString(), level);
    }

    this.updateCompanyProductTripsCategoryState();
  }

  private updateCompanyProductTripsCategoryState(): void {
    const selected_categories = this.selected_working_time_categories();
    const final_category = selected_categories[selected_categories.length - 1];
    this.is_company_product_trips_category = final_category?.slug === 'company-product-trips';
    this.shows_travel_time_checkbox =
      final_category != null && !this.is_company_product_trips_category && !this.is_company_product_category;

    if ((this.is_company_product_trips_category || this.is_company_product_category) && this.is_travel_time_checked) {
      this.is_travel_time_checked = false;
    }

    this.syncTripState();
  }

  onTravelTimeToggle(checked: boolean): void {
    this.is_travel_time_checked = checked;
    this.syncTripState();
  }

  private syncTripState(): void {
    const is_travel_time = this.is_travel_time_checked;
    this.should_show_location_selectors = this.is_company_product_trips_category || is_travel_time;

    if (this.is_company_product_trips_category) {
      this.fetchCompanyProductTripsLocations();

      return;
    }

    if (is_travel_time) {
      this.fetchTravelTimeLocations();

      return;
    }

    // The ECR is not a trip: clear any previously selected locations.
    this.form.patchValue({ origin_location_id: null, destination_location_id: null });
    this.locations.set([]);
  }

  private fetchSelectableLocations(): void {
    this.loading_locations.set(true);
    this.api.getLocations({ scopes: 'relatedToUser' }).subscribe({
      next: response => this.onLocationsFetched(response.data ?? []),
      error: () => this.onLocationsFetchError(),
    });
  }

  private fetchCompanyProductTripsLocations(): void {
    if (!this.is_company_product_trips_category) {
      this.locations.set([]);

      return;
    }

    this.fetchSelectableLocations();
  }

  private fetchTravelTimeLocations(): void {
    if (!this.is_travel_time_checked) {
      this.locations.set([]);

      return;
    }

    this.fetchSelectableLocations();
  }

  private async onLocationsFetched(locations: Location[]): Promise<void> {
    let locations_fetched = this.toLocationOptions(locations);

    if (this.employee.isMeetingOngoing()) {
      const meeting_mode_company_product_id = await firstValueFrom(this.employee.meetingModeCompanyProduct);
      const meeting_mode_company_product = await firstValueFrom(this.api.getCompanyProduct(meeting_mode_company_product_id.toString()));
      locations_fetched = locations_fetched.filter(
        location =>
          location.relatable_type === LocationRelatableTypeEnum.Company && location.relatable_id === meeting_mode_company_product.company_id
      );
    }

    this.locations.set(locations_fetched);
    this.loading_locations.set(false);
    this.form.patchValue(
      {
        origin_location_id: this.toLocationId(this.form.value.origin_location_id),
        destination_location_id: this.toLocationId(this.form.value.destination_location_id),
      },
      { emitEvent: false }
    );
    this.initDrivingRequestExists();
  }

  private onLocationsFetchError(): void {
    this.locations.set([]);
    this.loading_locations.set(false);
  }

  private toLocationOptions(locations: Location[]): LocationCustom[] {
    return locations.map(location => ({
      ...location,
      type_label: getLocationRelatableTypeLabel(location.relatable_type),
    }));
  }

  private getWorkingTimeCategoryById(id: string): WorkingTimeCategory | null {
    const from_lookup = this.working_time_category_lookup.get(id);
    if (from_lookup != null) {
      return from_lookup;
    }

    for (const c of this.working_time_categories) {
      if (c.id.toString() == id) {
        this.working_time_category_lookup.set(id, c);
        return c;
      }
    }

    for (const c of this.company_product_categories) {
      if (c.id.toString() == id) {
        this.working_time_category_lookup.set(id, c);
        return c;
      }
    }

    for (const c of this.all_working_time_categories) {
      if (c.id.toString() == id) {
        this.working_time_category_lookup.set(id, c);
        return c;
      }
    }

    for (const c of this.selected_working_time_categories()) {
      if (c.id.toString() == id) {
        this.working_time_category_lookup.set(id, c);
        return c;
      }
    }

    const available_by_level = this.available_working_time_categories_by_level();
    for (const categories of available_by_level.values()) {
      for (const c of categories) {
        if (c.id.toString() == id) {
          this.working_time_category_lookup.set(id, c);
          return c;
        }
      }
    }

    return null;
  }

  private async autoSelectCompanyProductPhase() {
    let singlePhase: CompanyProductPhase;
    if (this.filtered_company_product_phases.length === 0) {
      const data = await lastValueFrom(
        this.api.getCompanyProductPhases({ company_product_id: this.form.value['company_product_id'], is_active: true })
      );
      this.filtered_company_product_phases = [...data.data];
    }
    if (this.filtered_company_product_phases.length === 1) {
      singlePhase = this.filtered_company_product_phases[0];
      this.companyProductPhaseControl.setValue(singlePhase);
      this.selectCompanyProductPhase(singlePhase);
    }
  }

  selectCompanyProductPhase(p: CompanyProductPhase) {
    this.selectedCompanyProductPhase = p;
    // If we don't have the company_product yet, fetch it
    if (!this.selectedCompanyProduct && p.company_product_id) {
      this.api.getCompanyProduct(p.company_product_id.toString()).subscribe(company_product => {
        this.selectedCompanyProduct = company_product;
        void this.cacheProductMemberIds(p.company_product_id!);
      });
    } else if (p.company_product_id) {
      void this.cacheProductMemberIds(p.company_product_id);
    }
    this.form.patchValue({
      company_product_phase_id: p.id,
      relatable_id: p.id,
      relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProductPhase,
    });

    if (!this.is_hydrating_category_tree) {
      this.form.markAsDirty();
      this.companyProductPhaseControl.markAsDirty();
    }
  }

  private resetCompanyProductSubcategories(root_category: WorkingTimeCategory): void {
    this.selected_working_time_categories.set([root_category]);
    this.clearAvailableWorkingTimeCategoriesFromLevel(0);
    this.form.patchValue({
      working_time_category_level_1: '',
      working_time_category_level_2: '',
      working_time_category_level_3: '',
      working_time_category_level_4: '',
      working_time_category: root_category,
      working_time_category_id: root_category.id,
      company_product_phase_id: '',
    });
    this.companyProductPhaseControl.setValue('');
    this.selectedCompanyProductPhase = undefined;
    this.is_company_product_phase_category = false;
  }

  private fetchWorkingTimeCategoryChildren(id: string, level: number): void {
    this.working_time_category_children_changes.next({
      type: 'load',
      id,
      level,
      company_product_id: this.form.value['company_product_id'],
    });
  }

  private clearAvailableWorkingTimeCategoriesFromLevel(level: number): void {
    this.working_time_category_children_changes.next({ type: 'clear', level });
  }

  private updateAvailableWorkingTimeCategoriesByLevel(
    categories_by_level: Map<number, WorkingTimeCategory[]>,
    update: WorkingTimeCategoryChildrenUpdate
  ): Map<number, WorkingTimeCategory[]> {
    if (update.type === 'loaded') {
      const selected_category = this.selected_working_time_categories()[update.level];

      if (selected_category?.id.toString() !== update.id.toString()) {
        return categories_by_level;
      }

      this.registerWorkingTimeCategories(update.categories);
    }

    const updated_categories_by_level = new Map(categories_by_level);

    if (update.type === 'loaded' && update.categories.length > 0) {
      updated_categories_by_level.set(update.level, update.categories);
    } else {
      for (const available_level of updated_categories_by_level.keys()) {
        if (available_level >= update.level) {
          updated_categories_by_level.delete(available_level);
        }
      }
    }

    return updated_categories_by_level;
  }

  private fetchNotifications(): void {
    const ecr = this.getEventEcr();
    const event_meta = (this.event ?? this.data.calendarComponent.event_selected)?.meta;

    if (event_meta?.changed && ecr?.employee_calendar_register_notification_requests !== undefined) {
      this.notifications = this.parseNotificationsFromEcr(ecr.employee_calendar_register_notification_requests);
      return;
    }

    const rawId: number | string | undefined = ecr?.id;
    if (!rawId) return;

    let eventId: number | null = null;

    if (typeof rawId === 'string') {
      if ((rawId as string).startsWith('tmp_')) return;
      const parsed = parseInt(rawId, 10);
      if (!isNaN(parsed) && parsed > 0) {
        eventId = parsed;
      }
    } else if (typeof rawId === 'number' && rawId > 0) {
      eventId = rawId;
    }

    if (eventId === null) return;

    this.loadNotificationsForEvent(eventId);
  }

  private getEventEcr(): EmployeeCalendarRegisterWithRelationsSerialized | undefined {
    return this.event?.meta?.ecr ?? this.data.calendarComponent.event_selected?.meta?.ecr;
  }

  private parseNotificationsFromEcr(
    requests: EmployeeCalendarRegisterNotificationRequestPayload[]
  ): EmployeeCalendarRegisterNotificationRequest[] {
    const ecrId = this.getEventEcr()?.id;

    return requests
      .filter(request => !request._delete && request.minutes_before != null && request.channels != null)
      .map(request => ({
        id: request.id ?? 0,
        user_id: request.user_id ?? this.data.user_id,
        employee_calendar_register_id: typeof ecrId === 'number' ? ecrId : 0,
        minutes_before: request.minutes_before!,
        channels: request.channels!,
        notified_at: request.notified_at,
        created_at: request.created_at ?? new Date(),
        updated_at: request.updated_at ?? new Date(),
      }));
  }

  private syncNotificationsToParent(): void {
    const ecr = this.transformFormToEcrSerialized();
    if (ecr.from_date != null && ecr.to_date != null && ecr.working_time_category_id != null) {
      if (!isNaN(ecr.from_date.getTime()) && !isNaN(ecr.to_date.getTime())) {
        this.data.calendarComponent.onSelectedEventModified(ecr);
      }
    }
  }

  private loadNotificationsForEvent(eventId: number): void {
    this.loadingNotifications = true;
    this.api.getEmployeeCalendarRegisterNotificationRequests(eventId, this.data.user_id).subscribe({
      next: notifications => {
        this.notifications = notifications;
        this.deletedNotificationIds = [];
        this.loadingNotifications = false;
      },
      error: () => {
        this.loadingNotifications = false;
      },
    });
  }

  private initDrivingRequestExists(): void {
    const origin = this.form.controls['origin_location_id'].value;
    const destination = this.form.controls['destination_location_id'].value;
    if (!origin && !destination) return;

    const ecr_id = this.getEventEcr()?.id;
    if (!ecr_id) return;
    this.api
      .getDrivingRequests({ employee_calendar_register_id: ecr_id })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (data: ArrayResponse<DrivingRequest>) => {
          this.driving_request_exists.set(data.data.length > 0);
        },
      });
  }
}

type CategoryRequirementErrors = {
  categoryRequired?: true;
  companyProductRequired?: true;
  companyProductPhaseRequired?: true;
  originLocationRequired?: true;
  destinationLocationRequired?: true;
  travelCategoryRequired?: true;
  subcategoryRequired?: {
    level: number;
    categoryName: string;
  };
};

type WorkingTimeCategorySelectionChange = {
  value: number | string | null;
};

type WorkingTimeCategorySubcategorySelectLevel = {
  level: number;
  form_control_name: string;
  label: string;
  categories: WorkingTimeCategory[];
};

type WorkingTimeCategoryChildrenChange =
  | { type: 'clear'; level: number }
  | { type: 'load'; id: string; level: number; company_product_id: number | string | null }
  | { type: 'seed'; id: string; level: number; categories: WorkingTimeCategory[] };

type WorkingTimeCategoryChildrenUpdate =
  | { type: 'clear'; level: number }
  | { type: 'loaded'; id: string; level: number; categories: WorkingTimeCategory[] };

interface LocationCustom extends Location {
  type_label: string;
}

function resolvePersistedEmployeeCalendarRegisterId(event: CalendarEventCustomized | undefined): number | null {
  const ecr_id = event?.meta.ecr?.id as number | string | null | undefined;
  if (ecr_id == null) {
    return null;
  }

  if (typeof ecr_id === 'string') {
    return null;
  }

  return ecr_id;
}
